/*
  Sleeves: the sleeve card in the cart (<trc-sleeve-offer>) and the sleeve
  popup after a deck is added to the cart.

  snippets/trc-sleeve-offer.liquid prints the cart's lines and the sleeve
  catalog as JSON. The catalog is every product of the sleeve product type
  ("Card Sleeves") in the sleeve collection (theme setting, else Accessories),
  plus the product pinned per size in theme settings. Nothing about a sleeve
  needs new admin data:
    brand            the product vendor
    size             custom.sleeve_size, else custom.card_size, else the size
                     whose setting pins it, else a word in the title (Mini;
                     Jumbo or Large; anything else standard)
    sleeves per pack custom.sleeves_per_pack, else "(50 Pack)" in the title
    colors           the variant option named Color or Colour (none: one color)
    swatch fill      the option value's Shopify swatch (color or image), else
                     a color word in its name (NAME_HEX); a size whose colors
                     all have a fill shows round swatches, else text pills
  A product whose size or pack count cannot be read is skipped (noted once in
  the console). The default per size is the shopper's remembered pick (see
  "remembered choice"), else the pinned product, else the first product with
  stock; its default color is its first variant with stock. With more than
  one product of a size, each brand is a button (name, pack size, price).
  The thumbnails a brand or color switch would show load in the background,
  at the size shown, once the popup or the card is on screen.

  Cards per deck line, first source that gives a count:
    1. the signed "_Deck validation" proof: base64url(JSON).signature, payload.count
    2. "_Custom images": "N custom cards|manifest url" (the snippet keeps the
       part before the "|")
    3. the "Decklist" tally (snippets/trc-decklist-card-total.liquid)
    4. Exact Quantity products: the variant's custom.maximum_cards
    5. premade lines only: the variant's custom.deck_card_count, then one exact
       "N Cards" product tag ("100 Cards", "612 Cards")
  The option size is never used. A line with no count is "unknown".

  Deck size: the variant's custom.card_size, then the product's, then the
  variant's size option and the titles. "MTG Size" (standard, MTG, poker) and
  a deck with no size data use standard sleeves, Mini uses Mini sleeves, Jumbo
  and Large use Jumbo sleeves (no line until a Jumbo sleeve product exists).
  Any other size (tarot, square) is unknown: it shows the check note.

  The cart card (cart page only): an optional suggestion in a dashed
  outline. Its top row is only "Optional add-on" and "No thanks"; under it,
  per size still short, the chooser: the size, what it needs, the product,
  brand and color (the remembered pick first), the packs (capped at the
  stock left), the total and "Add N packs to cart". "No thanks" hides the
  card for the rest of the visit (sessionStorage; with storage blocked,
  until the page reloads), leaving one small line in the same box:
  "Sleeve suggestion hidden." and "Undo", which brings it back.
  The cart drawer has no sleeve card: its <trc-sleeve-offer> only carries
  the cart data the popup reads, and never renders.

  Enough sleeves: per size, the sum over every sleeve line of that size (any
  brand or color) of quantity x sleeves per pack, against the cards of that
  size. The cart card's packs to add are the sleeves still missing for every
  deck in the cart divided by the chosen product's pack size (rounded up),
  capped at the stock left.
  The card only adds: the sleeves already in the cart count toward the need,
  and their quantity is changed on the cart's own lines. A size with enough
  sleeves shows nothing; with every size covered the card is hidden until the
  cart is short again. After an add that leaves the size short, its group
  stays as it is, with "Added 4 packs. Add 2 more packs to cover all your
  decks." in place of the need sentence and the new packs preset. After an
  add that covers the size, the group (or the whole card, when nothing else is
  short) says "Added" with a check mark and closes; the sleeves show as a
  cart line above it.

  The popup recommends sleeves for the add alone: the cards in the deck just
  added x the copies that add put in the cart, divided by the chosen product's
  pack size (rounded up), capped at the stock left. Other decks and sleeves
  already in the cart do not change it. It is skipped when the sleeves of that
  size already in the cart cover every counted card of that size in the cart.
  Copies added: the quantity the product form sent (read on submit) and the
  line's quantity before and after the add, whichever is smaller; with
  neither, the whole line (a background upload line is always new).

  Every sleeve add is /cart/add.js {id, quantity} (added on top of a line of
  the same sleeves, never set), with a timeout: a sleeve line never carries
  line item properties, so it can never become a print job.
*/
(function () {
  'use strict';
  if (window.TRCSleeveOffer) return;

  var SIZES = ['standard', 'mini', 'jumbo'];
  var SIZE_NAME = { standard: 'standard', mini: 'Mini', jumbo: 'Jumbo' };
  var DECK_TYPES = ['custom deck', 'creator deck', 'premade deck'];
  var MAX_CARDS_PER_DECK = 10000;
  var MAX_PACKS = 99;
  // Timeouts in ms: a cart request, the drawer section re-read after an add,
  // and how long to wait for the theme to open the drawer.
  var TIMEOUTS = { fetch: 10000, section: 3000, drawer: 5000 };
  // "No thanks" on the cart card: hidden for the rest of the visit.
  var DISMISS_KEY = 'trc-sleeve-offer:dismissed';
  // How long a product form's add is matched to the product-ajax:added after it.
  var SENT_TTL_MS = 2 * 60 * 1000;
  // In ms: how long "Added" shows after an add that covers a size,
  // before it closes; how long the "Added 4 packs. Add 2 more packs..." line
  // shows after an add that leaves a size short; the longest a close takes;
  // the fade back to the need sentence.
  var TIMING = { added: 2600, confirm: 6000, collapse: 450, fade: 200 };

  var COPY = {
    heading: 'Optional add-on',
    dismiss: 'No thanks',
    hidden: 'Sleeve suggestion hidden.',
    undo: 'Undo',
    popupTitle: 'Add sleeves?',
    popupAdded: 'Your deck is in your cart.',
    popupAddedMany: 'Your decks are in your cart.',
    popupAddedSet: 'Your set is in your cart.',
    popupAddedSets: 'Your sets are in your cart.',
    popupAddedMixed: 'Your decks and sets are in your cart.',
    popupAddedDeckAndSet: 'Your deck and set are in your cart.',
    popupDecline: 'No thanks',
    popupContinue: 'Continue',
    unknown: 'We could not count every deck. Check your sleeve count.',
    oddSize: 'Some of your decks are not a standard size. Check which sleeves fit them.',
    allSleeves: 'See all sleeves',
    added: 'Added',
    addedSpoken: 'Sleeves added to your cart.',
    error: 'Sleeves could not be added. Please try again.',
    offline: 'We could not reach your cart. Reload the page to check your sleeves.',
    popupOffline: 'We could not reach your cart. Check your cart before you add sleeves again.',
  };

  // ---------------------------------------------------------------- helpers

  function text(value) {
    return value == null ? '' : String(value).trim();
  }

  function lower(value) {
    return text(value).toLowerCase();
  }

  // A whole number above zero (a number or a digits-only string), else null.
  function positiveInt(value, max) {
    var number = typeof value === 'string' && /^\s*\d+\s*$/.test(value) ? Number(value) : value;
    return typeof number === 'number' && Number.isInteger(number) && number > 0 && number <= (max || MAX_CARDS_PER_DECK) ? number : null;
  }

  function quantityOf(value) {
    var number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
  }

  function hasTag(tags, wanted) {
    var key = lower(wanted);
    if (!key || !Array.isArray(tags)) return false;
    return tags.some(function (tag) { return lower(tag) === key; });
  }

  var warned = {};
  function warnOnce(key, message) {
    if (warned[key]) return;
    warned[key] = true;
    try { if (window.console && console.info) console.info('[sleeves] ' + message); } catch (error) { /* never throws */ }
  }

  // ------------------------------------------------------------ card counts

  function decodeBase64Url(value) {
    var base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    var binary = atob(base64);
    if (typeof TextDecoder === 'function') {
      var bytes = new Uint8Array(binary.length);
      for (var index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
      return new TextDecoder('utf-8').decode(bytes);
    }
    return decodeURIComponent(escape(binary));
  }

  // The proof payload ({v, count, variantId, mode, ...}) or null. The signature
  // is not checked here: checkout does that. Only the count is read.
  function decodeProof(token) {
    var parts = text(token).split('.');
    if (parts.length !== 2 || !/^[A-Za-z0-9_-]+$/.test(parts[0]) || !parts[1]) return null;
    try {
      var payload = JSON.parse(decodeBase64Url(parts[0]));
      return payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : null;
    } catch (error) {
      return null;
    }
  }

  function proofCount(token, variantId) {
    var payload = decodeProof(token);
    if (!payload) return null;
    // A proof issued for another variant is not this line's count.
    if (payload.variantId != null && variantId != null && text(payload.variantId) !== text(variantId)) return null;
    return positiveInt(payload.count);
  }

  function customImagesCount(value) {
    var match = /^\s*(\d+)\s+custom cards?\s*(?:\||$)/i.exec(text(value));
    return match ? positiveInt(match[1]) : null;
  }

  // One exact "N Cards" tag. "125+ Cards", no tag, or two different counts: unknown.
  function tagCount(tags) {
    if (!Array.isArray(tags)) return null;
    var found = null;
    for (var index = 0; index < tags.length; index++) {
      var match = /^(\d+) Cards$/.exec(text(tags[index]));
      if (!match) continue;
      var count = positiveInt(match[1]);
      if (count == null) continue;
      if (found != null && found !== count) return null;
      found = count;
    }
    return found;
  }

  function isPremadeLine(line, options) {
    var type = lower(line.type);
    if (type === 'premade deck') return true;
    if (lower(line.inputMethod) === 'premade reorder' || lower(line.reorder) === 'true') return true;
    return !type && hasTag(line.tags, (options && options.premadeTag) || 'Premade');
  }

  // Card sets say "set", not "deck": the line's noun from
  // snippets/trc-product-noun.liquid, else the same tag and type rule.
  var SET_TYPES = ['card set', 'card sets', 'set', 'sets', 'packs & sets'];
  function isSetLine(line) {
    if (!line) return false;
    if (text(line.noun)) return lower(line.noun) === 'set';
    var type = lower(line.type);
    if (type.indexOf('deck') !== -1) return false;
    return SET_TYPES.indexOf(type) !== -1 || hasTag(line.tags, 'Sets') || hasTag(line.tags, 'Packs & Sets');
  }

  // What the added lines of a popup plan are called: 'set' (card sets only),
  // 'deck' (no sets), or '' (both).
  function planNoun(plan) {
    var sets = (plan && plan.sets) || 0;
    return !sets ? 'deck' : sets >= plan.decks ? 'set' : '';
  }

  // Deck lines only: an "_Input method" property, or a deck product type (or,
  // with no type, the Creator Deck or premade tag, as the rest of the theme
  // decides). Card packs (no type, Premade tag) are printed cards: they count.
  function isDeckLine(line, options) {
    if (text(line.inputMethod)) return true;
    var type = lower(line.type);
    if (DECK_TYPES.indexOf(type) !== -1) return true;
    if (type) return false;
    return hasTag(line.tags, 'Creator Deck') || hasTag(line.tags, (options && options.premadeTag) || 'Premade');
  }

  // Cards in one copy of the line's deck, or null when it cannot be counted.
  function cardsForLine(line, options) {
    if (!line) return null;
    var count = proofCount(line.proof, line.variantId);
    if (count == null) count = customImagesCount(line.customImages);
    if (count == null) count = positiveInt(line.decklistCount);
    if (count == null) count = positiveInt(line.exactMax);
    if (count == null && isPremadeLine(line, options)) {
      count = positiveInt(line.deckCardCount);
      if (count == null) count = tagCount(line.tags);
    }
    return count;
  }

  // ------------------------------------------------------------------ sizes

  var JUMBO_WORDS = /\b(?:jumbo|large)\b|3\.5\s*(?:x|×)\s*5/;

  // An explicit size value ("MTG Size", "Mini", "Jumbo", a "... Size" option):
  // 'standard', 'mini', 'jumbo', or 'unknown' for any other size (tarot,
  // square). A blank value is standard.
  function classifySize(value) {
    var words = lower(value);
    if (!words) return 'standard';
    if (/\bmini\b/.test(words)) return 'mini';
    if (JUMBO_WORDS.test(words)) return 'jumbo';
    if (/\b(?:mtg|standard|poker)\b/.test(words)) return 'standard';
    return 'unknown';
  }

  function sizeOfLine(line) {
    var field = text(line.sizeVariant) || text(line.sizeProduct);
    if (field) return classifySize(field);
    // No size field: the variant's size option ("Mini", "Tarot Size") or a size
    // word in the titles. A deck with no size data at all is standard.
    var options = text(line.variantTitle).split('/').map(lower);
    var words = options.join(' ') + ' ' + lower(line.title);
    if (/\bmini\b/.test(words)) return 'mini';
    if (JUMBO_WORDS.test(words)) return 'jumbo';
    if (/\b(?:tarot|square)\b/.test(words)) return 'unknown';
    var odd = options.some(function (option) { return /\bsize$/.test(option) && classifySize(option) === 'unknown'; });
    return odd ? 'unknown' : 'standard';
  }

  // A sleeve product's size: custom.sleeve_size, else custom.card_size, else
  // the size whose theme setting pins it, else a word in the title (Mini;
  // Jumbo or Large; else standard). null when a size field holds a size we
  // do not sell sleeves for.
  function sleeveSizeOf(product) {
    if (!product) return null;
    var field = text(product.sleeveSize) || text(product.cardSize);
    if (field) {
      var size = classifySize(field);
      return size === 'unknown' ? null : size;
    }
    if (SIZES.indexOf(lower(product.pin)) !== -1) return lower(product.pin);
    var words = lower(product.title);
    if (/\bmini\b/.test(words)) return 'mini';
    if (JUMBO_WORDS.test(words)) return 'jumbo';
    return 'standard';
  }

  // Sleeves per pack: the custom.sleeves_per_pack metafield, else "(50 Pack)" in the title.
  function packSize(sleeve) {
    if (!sleeve) return null;
    var size = positiveInt(sleeve.perPack, 1000);
    if (size) return size;
    var match = /\((\d+)[\s-]*pack\)/i.exec(text(sleeve.title));
    return match ? positiveInt(match[1], 1000) : null;
  }

  // The index of the product option named Color or Colour, else -1.
  function colorIndexOf(product) {
    var names = Array.isArray(product && product.options) ? product.options : [];
    for (var index = 0; index < names.length; index++) {
      var name = lower(names[index] && typeof names[index] === 'object' ? names[index].name : names[index]);
      if (name === 'color' || name === 'colour') return index;
    }
    return -1;
  }

  // ----------------------------------------------------------------- colors

  // The fill for a color name when the option value has no Shopify swatch.
  // Keep in step with snippets/trc-swatch-color.liquid (the product page).
  var NAME_HEX = {
    black: '#111111', white: '#f5f5f5', gray: '#8a8d91', grey: '#8a8d91', blue: '#1f4fbf', teal: '#138f8f',
    green: '#2e8b3a', purple: '#5b2d8c', red: '#b3212b', yellow: '#f2c230', orange: '#e8731c', pink: '#e86a9e',
    lime: '#8cc63f', navy: '#1f2a5c', brown: '#6b4226', silver: '#c0c4c8', gold: '#c9a227',
  };
  var CLEAR_FILL = 'linear-gradient(135deg, #ffffff 0%, #e6ecf1 45%, #ffffff 55%, #eef2f5 100%)';

  function hexOf(rgb) {
    if (!Array.isArray(rgb) || rgb.length < 3) return '';
    var parts = rgb.slice(0, 3).map(function (part) { return Math.max(0, Math.min(255, Math.round(Number(part)))); });
    if (parts.some(function (part) { return !Number.isFinite(part); })) return '';
    return '#' + parts.map(function (part) { return (part < 16 ? '0' : '') + part.toString(16); }).join('');
  }

  // A light fill gets a darker ring so it shows on a white panel.
  function isLight(hex) {
    var match = /^#([0-9a-f]{6})$/i.exec(hex || '');
    if (!match) return false;
    var value = parseInt(match[1], 16);
    var luma = (0.299 * ((value >> 16) & 255) + 0.587 * ((value >> 8) & 255) + 0.114 * (value & 255)) / 255;
    return luma > 0.82;
  }

  // {fill, image, light} for a color option value: the Shopify swatch first
  // (option_value.swatch, from the product's category metafield), then the
  // name ("Matte Black": black), else null.
  function swatchFor(label, swatch) {
    if (swatch && text(swatch.image)) return { fill: '', image: text(swatch.image), light: false };
    var hex = swatch ? hexOf(swatch.rgb) : '';
    if (hex) return { fill: hex, image: '', light: isLight(hex) };
    // Words as Liquid's handleize splits them (snippets/trc-swatch-color.liquid):
    // runs of letters and digits, checked from the last.
    var words = lower(label).split(/[^a-z0-9]+/).filter(Boolean);
    for (var index = words.length - 1; index >= 0; index--) {
      if (words[index] === 'clear') return { fill: CLEAR_FILL, image: '', light: true };
      if (NAME_HEX[words[index]]) return { fill: NAME_HEX[words[index]], image: '', light: isLight(NAME_HEX[words[index]]) };
    }
    return null;
  }

  // ----------------------------------------------------------------- images

  // Shopify image URLs take a width parameter: the thumbnails are fetched at
  // the size shown (srcset, so 1x, 2x and 3x screens each get their own).
  var IMAGE_WIDTHS = [80, 160, 240];

  function shopifyImage(url) {
    return /(?:^|\/\/)[^\/]*(?:cdn\.shopify\.com|shopifycdn\.)|\/cdn\/shop\//.test(url);
  }

  function sizedUrl(url, width) {
    if (!shopifyImage(url)) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // {src, srcset, sizes, key} for a thumbnail shown `shown` CSS px wide.
  function imageSource(url, shown) {
    var src = text(url);
    if (!src) return null;
    if (!shopifyImage(src)) return { src: src, srcset: '', sizes: '', key: src };
    var srcset = IMAGE_WIDTHS.map(function (width) { return sizedUrl(src, width) + ' ' + width + 'w'; }).join(', ');
    var sizes = shown + 'px';
    return { src: sizedUrl(src, IMAGE_WIDTHS[1]), srcset: srcset, sizes: sizes, key: srcset + '|' + sizes };
  }

  function applySource(img, source) {
    // sizes and srcset before src, so the browser fetches one candidate only.
    if (source.srcset) { img.sizes = source.sizes; img.srcset = source.srcset; }
    img.src = source.src;
  }

  // Decoded thumbnails, kept so a later swap paints at once.
  var decodedImages = {};

  // Fetches and decodes thumbnails in the background, two at a time, at low
  // priority, after the page is idle. cancel() drops the queue and stops the
  // fetches still out.
  function preloader() {
    var queue = [];
    var active = [];
    var cancelled = false;
    var scheduled = false;
    function idle(run) {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 1500 });
      else window.setTimeout(run, 250);
    }
    function pump() {
      scheduled = false;
      while (!cancelled && active.length < 2 && queue.length) {
        var source = queue.shift();
        if (!decodedImages[source.key]) load(source);
      }
    }
    function load(source) {
      var img = new Image();
      var job = { img: img, done: false };
      active.push(job);
      try { img.fetchPriority = 'low'; } catch (error) { /* older browsers */ }
      img.decoding = 'async';
      function finish(ok) {
        if (job.done) return;
        job.done = true;
        active = active.filter(function (item) { return item !== job; });
        if (ok && !cancelled) decodedImages[source.key] = img;
        if (!cancelled) idle(pump);
      }
      var ready = typeof img.decode === 'function'
        ? null
        : new Promise(function (resolve, reject) { img.onload = resolve; img.onerror = reject; });
      applySource(img, source);
      (ready || img.decode()).then(function () { finish(true); }, function () { finish(false); });
    }
    return {
      add: function (sources) {
        if (cancelled) return;
        sources.forEach(function (source) {
          if (!source || decodedImages[source.key]) return;
          if (queue.some(function (item) { return item.key === source.key; })) return;
          queue.push(source);
        });
        if (!scheduled && queue.length) { scheduled = true; idle(pump); }
      },
      cancel: function () {
        cancelled = true;
        queue = [];
        active.forEach(function (job) {
          job.done = true;
          try { job.img.removeAttribute('srcset'); job.img.src = ''; } catch (error) { /* gone */ }
        });
        active = [];
      },
    };
  }

  // The thumbnails a brand or color switch can show for a size, the ones a
  // tap away first: the other brands' default color, then the colors of the
  // chosen brand, then the other brands' colors.
  function switchImages(info, current, inCart, shown) {
    var urls = [];
    function push(entry, variant) {
      var url = (variant && variant.image) || entry.image;
      if (url && urls.indexOf(url) === -1) urls.push(url);
    }
    var currentUrl = current ? (current.variant.image || current.entry.image) : '';
    var others = info.products.filter(function (entry) { return !current || entry !== current.entry; });
    others.forEach(function (entry) { var first = firstSelectable(entry, inCart); if (first) push(entry, first); });
    if (current) current.entry.variants.forEach(function (variant) { if (selectable(variant, inCart)) push(current.entry, variant); });
    others.forEach(function (entry) {
      if (!firstSelectable(entry, inCart)) return;
      entry.variants.forEach(function (variant) { if (selectable(variant, inCart)) push(entry, variant); });
    });
    return urls.filter(function (url) { return url !== currentUrl; }).slice(0, 24).map(function (url) { return imageSource(url, shown); });
  }

  // ------------------------------------------------------- remembered choice

  // The brand and color a shopper last picked, per size, become their default
  // next time: a first-party cookie (and localStorage) on this browser, and,
  // signed in, the customer metafield custom.sleeve_preference through the
  // theme setting's endpoint. When the store's cookie banner (Shopify's
  // customer privacy API) says preferences may not be stored, picks stay in
  // memory for this page and are stored only once the shopper allows it. {standard: {p: product id, b: brand, c: color,
  // t: time, a: 1 when picked signed in}}.
  var PREF_COOKIE = 'trc_sleeve_pref';
  var PREF_KEY = 'trc-sleeve-pref';
  var PREF_MAX_AGE = 365 * 24 * 60 * 60;

  function cleanPref(value) {
    var out = {};
    if (typeof value === 'string') { try { value = JSON.parse(value); } catch (error) { value = null; } }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return out;
    SIZES.forEach(function (size) {
      var item = value[size];
      if (!item || typeof item !== 'object') return;
      var pick = { p: text(item.p).slice(0, 32), b: text(item.b).slice(0, 80), c: text(item.c).slice(0, 80), t: positiveInt(item.t, 1e14) || 0 };
      if (item.a) pick.a = 1;
      if (pick.p || pick.b) out[size] = pick;
    });
    return out;
  }

  function readCookiePref() {
    try {
      var match = new RegExp('(?:^|;\\s*)' + PREF_COOKIE + '=([^;]*)').exec(document.cookie || '');
      return match ? cleanPref(decodeURIComponent(match[1])) : {};
    } catch (error) {
      return {};
    }
  }

  function readLocalPref() {
    try { return cleanPref(window.localStorage.getItem(PREF_KEY)); } catch (error) { return {}; }
  }

  // Picks of this page view, kept in memory: the only copy while the shopper
  // has not allowed preference storage, written out if they allow it later.
  var memoryPref = {};
  var pendingPref = false;

  // This browser's choice per size: the newest of the cookie, localStorage
  // and this page's picks.
  function browserPref() {
    var sources = [readCookiePref(), readLocalPref(), memoryPref];
    var out = {};
    SIZES.forEach(function (size) {
      var pick = null;
      sources.forEach(function (source) {
        var item = source[size];
        if (item && (!pick || (item.t || 0) > (pick.t || 0))) pick = item;
      });
      if (pick) out[size] = pick;
    });
    return out;
  }

  // Shopify's customer privacy API, when the store loads it (a cookie
  // banner): may preferences be stored on this device? Without the API the
  // store asks for no consent, so they may.
  function preferencesAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return true;
      return privacy.preferencesProcessingAllowed() !== false;
    } catch (error) {
      return true;
    }
  }

  function storeBrowserPref(value) {
    try {
      var secure = window.location && window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = PREF_COOKIE + '=' + encodeURIComponent(value) + '; Max-Age=' + PREF_MAX_AGE + '; Path=/; SameSite=Lax' + secure;
    } catch (error) { /* cookies blocked */ }
    try { window.localStorage.setItem(PREF_KEY, value); } catch (error) { /* storage blocked */ }
  }

  function clearBrowserPref() {
    try { document.cookie = PREF_COOKIE + '=; Max-Age=0; Path=/; SameSite=Lax'; } catch (error) { /* cookies blocked */ }
    try { window.localStorage.removeItem(PREF_KEY); } catch (error) { /* storage blocked */ }
  }

  function writeBrowserPref(pref) {
    memoryPref = cleanPref(pref);
    if (!preferencesAllowed()) { pendingPref = true; return; }
    pendingPref = false;
    storeBrowserPref(JSON.stringify(memoryPref));
  }

  // The shopper answered the cookie banner: store this page's picks if
  // preferences are now allowed, drop the stored ones if they are not.
  function onConsentCollected(event) {
    var detail = (event && event.detail) || {};
    var allowed = typeof detail.preferencesAllowed === 'boolean' ? detail.preferencesAllowed : preferencesAllowed();
    if (!allowed) { clearBrowserPref(); return; }
    if (pendingPref) { pendingPref = false; storeBrowserPref(JSON.stringify(browserPref())); }
  }

  function accountPref(data) {
    return data && data.customer ? cleanPref(data.customer.pref) : {};
  }

  // The remembered pick for a size. Signed in, the account's wins over a pick
  // made signed out; a pick made signed in on this browser that is newer
  // than the account's (its save did not land yet) wins over it.
  function prefFor(size, data) {
    var local = browserPref()[size] || null;
    if (!data || !data.customer) return local;
    var account = accountPref(data)[size] || null;
    if (account && local && local.a && (local.t || 0) > (account.t || 0)) return local;
    return account || local;
  }

  // The remembered pick as a choice for this size's products, or null when
  // its brand is gone. A sold out brand or color falls back in resolveChoice.
  function prefChoice(info, pick) {
    if (!pick) return null;
    var entry = info.products.filter(function (product) { return pick.p && text(product.productId) === pick.p; })[0] ||
      info.products.filter(function (product) { return pick.b && lower(product.brand) === lower(pick.b); })[0] || null;
    if (!entry) return null;
    var variant = entry.variants.filter(function (item) { return pick.c && lower(item.label) === lower(pick.c); })[0] || null;
    return { productId: entry.productId, variantId: variant ? variant.id : null };
  }

  // This page's pick for a size, else the remembered one.
  function choiceFor(info, data) {
    return state.choice[info.size] || prefChoice(info, prefFor(info.size, data));
  }

  var accountSync = { timer: null, merged: false };

  // The endpoint as a path on this site, or '' (another origin, a
  // protocol-relative or backslash trick, a URL that does not parse).
  function sameOriginPath(value) {
    var raw = text(value);
    if (!raw || /[\u0000-\u001f\u007f]/.test(raw)) return '';
    try {
      var origin = window.location.origin;
      var url = new URL(raw, origin);
      if (url.origin !== origin) return '';
      return url.pathname + url.search;
    } catch (error) {
      return '';
    }
  }

  // Saves the browser's picks to the customer account (signed in, with an
  // endpoint set). Fire and forget: the browser copy is the fallback.
  function pushAccountPref(data, delay) {
    var endpoint = sameOriginPath(data && data.prefEndpoint);
    if (!data || !data.customer || !endpoint) return;
    window.clearTimeout(accountSync.timer);
    accountSync.timer = window.setTimeout(function () {
      var body = JSON.stringify({ preference: browserPref() });
      timedFetch(endpoint, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body })
        .then(null, function () { /* the browser copy stays */ });
    }, delay == null ? 800 : delay);
  }

  // Once per page, signed in: sizes the account has no pick for take this
  // browser's (pushed up); sizes it has a pick for are copied down, so the
  // browser follows the account.
  function mergeAccountPref(data) {
    if (accountSync.merged || !data || !data.customer) return;
    accountSync.merged = true;
    var account = accountPref(data);
    var local = browserPref();
    var push = false;
    var next = {};
    SIZES.forEach(function (size) {
      var mine = local[size];
      var theirs = account[size];
      if (theirs && !(mine && mine.a && (mine.t || 0) > (theirs.t || 0))) next[size] = Object.assign({}, theirs, { a: 1 });
      else if (mine) {
        // Kept as it was (its time, and signed out if it was): the push
        // saves it to the account (or resends a signed in pick not saved yet).
        next[size] = mine;
        push = true;
      }
    });
    if (Object.keys(next).length) writeBrowserPref(next);
    if (push) pushAccountPref(data, 0);
  }

  // Remembers a pick for its size, on this browser and on the account.
  function rememberChoice(size, entry, variant, data) {
    if (!size || !entry) return;
    var pref = browserPref();
    pref[size] = { p: text(entry.productId), b: entry.brand, c: variant && entry.variants.length > 1 ? variant.label : '', t: Date.now() };
    if (data && data.customer) pref[size].a = 1;
    writeBrowserPref(pref);
    pushAccountPref(data);
  }

  // ---------------------------------------------------------------- catalog

  // {list, byProduct, byVariant} from data.catalog. Each entry:
  // {productId, title, brand, url, image, size, perPack, pin, colorIndex, variants}
  // and each variant {id, label, price, available, inventory, image, swatch}.
  function buildCatalog(data) {
    var list = [];
    var byProduct = {};
    var byVariant = {};
    (Array.isArray(data && data.catalog) ? data.catalog : []).forEach(function (product) {
      if (!product || product.productId == null || byProduct[text(product.productId)]) return;
      var size = sleeveSizeOf(product);
      var perPack = packSize(product);
      if (!size || !perPack) {
        warnOnce('skip-' + product.productId, 'Skipped "' + text(product.title) + '": ' + (!size ? 'its sleeve size is not standard, Mini or Jumbo.' : 'add custom.sleeves_per_pack or "(N Pack)" to its title.'));
        return;
      }
      var colorIndex = colorIndexOf(product);
      var swatches = product.swatches && typeof product.swatches === 'object' ? product.swatches : {};
      var variants = (Array.isArray(product.variants) ? product.variants : []).filter(function (variant) {
        return variant && variant.id != null;
      }).map(function (variant) {
        var options = Array.isArray(variant.options) ? variant.options : [];
        var label = colorIndex !== -1 ? text(options[colorIndex]) : '';
        if (!label && text(variant.title) && lower(variant.title) !== 'default title') label = text(variant.title);
        var inventory = variant.inventory != null && Number.isFinite(Number(variant.inventory)) ? Math.floor(Number(variant.inventory)) : null;
        return {
          id: variant.id,
          label: label,
          price: quantityOf(variant.price),
          available: variant.available !== false && (inventory == null || inventory > 0),
          inventory: inventory,
          image: text(variant.image),
          swatch: colorIndex !== -1 && label ? swatchFor(label, Object.prototype.hasOwnProperty.call(swatches, label) ? swatches[label] : null) : null,
        };
      });
      if (!variants.length) return;
      var entry = {
        productId: product.productId,
        title: text(product.title),
        brand: text(product.vendor) || text(product.title),
        url: text(product.url),
        image: text(product.image),
        size: size,
        perPack: perPack,
        pin: lower(product.pin),
        colorIndex: colorIndex,
        variants: variants,
      };
      list.push(entry);
      byProduct[text(entry.productId)] = entry;
      variants.forEach(function (variant) { byVariant[text(variant.id)] = { entry: entry, variant: variant }; });
    });
    return { list: list, byProduct: byProduct, byVariant: byVariant };
  }

  // A sleeve line whose product is not in the catalog (left the collection),
  // read from the line itself.
  function lineEntry(line) {
    var size = sleeveSizeOf({ sleeveSize: line.sleeveSize, cardSize: line.sizeProduct, title: line.title });
    var perPack = packSize({ perPack: line.perPack, title: line.title });
    if (!size || !perPack) return null;
    var variant = { id: line.variantId, label: lower(line.variantTitle) === 'default title' ? '' : text(line.variantTitle), price: quantityOf(line.price), available: false, inventory: null, image: text(line.image) };
    return { productId: line.productId, title: text(line.title), brand: text(line.vendor) || text(line.title), url: text(line.url), image: text(line.image), size: size, perPack: perPack, pin: '', colorIndex: -1, variants: [variant] };
  }

  // ------------------------------------------------------------- pack math

  function stockLeft(variant, inCart) {
    if (!variant || !variant.available) return 0;
    if (variant.inventory == null) return Infinity;
    return Math.max(0, variant.inventory - (inCart[text(variant.id)] || 0));
  }

  function selectable(variant, inCart) {
    return stockLeft(variant, inCart) > 0;
  }

  function firstSelectable(entry, inCart) {
    for (var index = 0; index < entry.variants.length; index++) {
      if (selectable(entry.variants[index], inCart)) return entry.variants[index];
    }
    return null;
  }

  // Per size: the cards, the sleeves already in the cart and the products.
  // {sizes: [info], catalog, inCart}. info:
  //   {size, cards, unknown, sleeveLines, sleevesHave, missing, covered,
  //    products, defaultProductId}
  // A size is listed when the cart has decks of that size and either a sleeve
  // line of that size or a product of that size with stock.
  function computeSizes(data) {
    data = data || {};
    var options = { premadeTag: data.premadeTag || 'Premade' };
    var sleeveType = lower(data.sleeveType || 'Card Sleeves');
    var catalog = buildCatalog(data);
    var inCart = {};
    var pools = {};
    SIZES.forEach(function (size) { pools[size] = { cards: 0, unknown: 0, odd: 0, sleeveLines: [] }; });
    var lines = Array.isArray(data.lines) ? data.lines : [];

    lines.forEach(function (line) {
      if (!line || !quantityOf(line.quantity)) return;
      inCart[text(line.variantId)] = (inCart[text(line.variantId)] || 0) + quantityOf(line.quantity);
    });

    lines.forEach(function (line) {
      if (!line) return;
      var quantity = quantityOf(line.quantity);
      if (!quantity) return;
      var known = catalog.byProduct[text(line.productId)];
      if (known || lower(line.type) === sleeveType) {
        var entry = known || lineEntry(line);
        if (!entry) return;
        var found = catalog.byVariant[text(line.variantId)];
        var variant = found && found.entry === entry ? found.variant : null;
        if (!variant) variant = lineEntry(line) ? lineEntry(line).variants[0] : { id: line.variantId, label: '', price: 0, available: false, inventory: null, image: '' };
        pools[entry.size].sleeveLines.push({ key: text(line.key), variantId: line.variantId, quantity: quantity, entry: entry, variant: variant });
        return;
      }
      if (!isDeckLine(line, options)) return;
      var size = sizeOfLine(line);
      // A tarot, square or other size: never counted toward any sleeves, only
      // the check note (on the standard group, as most of our sleeves are standard).
      if (size === 'unknown') {
        pools.standard.odd += quantity;
        return;
      }
      var cards = cardsForLine(line, options);
      if (cards == null) pools[size].unknown += quantity;
      else pools[size].cards += cards * quantity;
    });

    var sizes = [];
    SIZES.forEach(function (size) {
      var pool = pools[size];
      if (!pool.cards && !pool.unknown && !pool.odd) return;
      var products = catalog.list.filter(function (entry) { return entry.size === size; });
      // The pinned product first, then the collection order.
      products.sort(function (a, b) { return (b.pin === size ? 1 : 0) - (a.pin === size ? 1 : 0); });
      var withStock = products.filter(function (entry) { return firstSelectable(entry, inCart); });
      if (!pool.sleeveLines.length && !withStock.length) return;
      var pinned = products.filter(function (entry) { return entry.pin === size; })[0];
      var preferred = pinned && firstSelectable(pinned, inCart) ? pinned : (withStock[0] || pinned || products[0] || null);
      var have = pool.sleeveLines.reduce(function (sum, line) { return sum + line.quantity * line.entry.perPack; }, 0);
      var missing = Math.max(0, pool.cards - have);
      sizes.push({
        size: size,
        cards: pool.cards,
        unknown: pool.unknown,
        odd: pool.odd,
        sleeveLines: pool.sleeveLines,
        sleevesHave: have,
        missing: missing,
        covered: pool.cards > 0 && missing === 0,
        products: products,
        defaultProductId: preferred ? preferred.productId : null,
      });
    });
    return { sizes: sizes, catalog: catalog, inCart: inCart };
  }

  // Packs of this product that cover the sleeves still missing (0 when
  // covered), and what can be added of this variant now (capped at stock).
  function recommend(info, entry, variant, inCart) {
    var packs = info && entry && info.missing > 0 ? Math.ceil(info.missing / entry.perPack) : 0;
    var left = stockLeft(variant, inCart || {});
    return { packs: packs, offer: Math.min(packs, left), stockLeft: left, partial: packs > 0 && left < packs };
  }

  // The product and variant to show for a size: the shopper's choice from
  // this page if it still has stock, else the default.
  function resolveChoice(info, inCart, choice) {
    var entry = null;
    if (choice) {
      entry = info.products.filter(function (product) { return text(product.productId) === text(choice.productId); })[0] || null;
      if (entry && !firstSelectable(entry, inCart)) entry = null;
    }
    if (!entry) entry = info.products.filter(function (product) { return text(product.productId) === text(info.defaultProductId); })[0] || null;
    if (!entry) return null;
    var variant = null;
    if (choice && text(choice.productId) === text(entry.productId)) {
      variant = entry.variants.filter(function (item) { return text(item.id) === text(choice.variantId) && selectable(item, inCart); })[0] || null;
    }
    if (!variant) variant = firstSelectable(entry, inCart) || entry.variants[0];
    return { entry: entry, variant: variant };
  }

  function itemMatches(item, line) {
    return item.key != null ? text(item.key) === text(line.key) : text(item.variant_id) === text(line.variantId);
  }

  // Copies of a deck one add put in the cart. item.requested is the quantity
  // the product form sent; item.before is the line's quantity before the add
  // (0 for a new line). The smaller of the two that are known, never more
  // than the line holds now unless the line data is older than the add.
  function addedQuantity(item, line) {
    var total = quantityOf(line.quantity);
    var asked = positiveInt(item && item.requested, MAX_CARDS_PER_DECK);
    var diff = item && item.before != null ? total - quantityOf(item.before) : 0;
    if (asked) return diff > 0 ? Math.min(asked, diff) : asked;
    return diff > 0 ? diff : Math.max(1, total);
  }

  // The popup plan for the lines an add put in the cart, or null when no
  // popup: the added deck must be counted, the sleeves of its size already
  // in the cart must not cover every counted card of that size in the cart,
  // and a product of that size must have stock. deckCards and decks are for
  // the copies this add put in the cart only.
  function popupPlan(data, items) {
    if (!data || !items || !items.length) return null;
    var options = { premadeTag: data.premadeTag || 'Premade' };
    var added = [];
    (Array.isArray(data.lines) ? data.lines : []).forEach(function (line) {
      if (!line || !isDeckLine(line, options) || cardsForLine(line, options) == null) return;
      var item = items.filter(function (candidate) { return candidate && itemMatches(candidate, line); })[0];
      if (item) added.push({ line: line, copies: addedQuantity(item, line) });
    });
    // Theme setting "Popup only for decks with at least N cards" (0: every
    // deck and card pack).
    var minCards = quantityOf(data.popupMinCards);
    if (minCards) added = added.filter(function (pick) { return cardsForLine(pick.line, options) >= minCards; });
    if (!added.length) return null;
    var result = computeSizes(data);
    for (var index = 0; index < added.length; index++) {
      var size = sizeOfLine(added[index].line);
      var info = result.sizes.filter(function (item) { return item.size === size; })[0];
      if (!info || info.covered || info.missing <= 0) continue;
      var hasStock = info.products.some(function (entry) { return firstSelectable(entry, result.inCart); });
      if (!hasStock) continue;
      var deckLines = added.filter(function (pick) { return sizeOfLine(pick.line) === size; });
      var deckCards = deckLines.reduce(function (sum, pick) { return sum + cardsForLine(pick.line, options) * pick.copies; }, 0);
      var decks = deckLines.reduce(function (sum, pick) { return sum + pick.copies; }, 0);
      var sets = deckLines.reduce(function (sum, pick) { return sum + (isSetLine(pick.line) ? pick.copies : 0); }, 0);
      return { size: size, info: info, inCart: result.inCart, deckCards: deckCards, decks: decks, sets: sets };
    }
    return null;
  }

  // The popup's packs for the chosen product: the added decks' cards only
  // (sleeves already in the cart do not lower it), capped at the stock left.
  function popupRecommend(plan, entry, variant, inCart) {
    var packs = plan && entry && plan.deckCards > 0 ? Math.ceil(plan.deckCards / entry.perPack) : 0;
    var left = stockLeft(variant, inCart || {});
    return { packs: packs, offer: Math.min(packs, left), stockLeft: left, partial: packs > 0 && left < packs };
  }

  // Does an added deck get a popup?
  function addGetsOffer(data, items) {
    return Boolean(popupPlan(data, items));
  }

  // ------------------------------------------------------------------- copy

  function packs(count) {
    return count + (count === 1 ? ' pack' : ' packs');
  }

  function cardsText(count, size) {
    return count + (size === 'mini' ? ' Mini' : size === 'jumbo' ? ' Jumbo' : '') + (count === 1 ? ' card' : ' cards');
  }

  function sizeTitle(size) {
    return size === 'mini' ? 'Mini sleeves' : size === 'jumbo' ? 'Jumbo sleeves' : 'Standard sleeves';
  }

  function simpleMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = placeholder.exec(format || '');
    var kind = match ? match[1] : 'amount';
    var decimals = /no_decimals/.test(kind) ? 0 : 2;
    var amount = (cents / 100).toFixed(decimals);
    var parts = amount.split('.');
    var comma = /comma_separator/.test(kind);
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
    var value = whole + (parts[1] ? (comma ? ',' : '.') + parts[1] : '');
    return match ? format.replace(placeholder, value) : value;
  }

  // The theme's money helper and the shop's money format (FoxTheme.settings.moneyFormat).
  function formatMoney(cents, fallbackFormat) {
    var theme = window.FoxTheme || {};
    var format = (theme.settings && theme.settings.moneyFormat) || fallbackFormat || '';
    var formatted = '';
    if (format && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      try { formatted = theme.Currency.formatMoney(cents, format); } catch (error) { formatted = ''; }
    }
    if (!formatted) formatted = simpleMoney(cents, format);
    return String(formatted).replace(/<[^>]*>/g, '').trim();
  }

  // What a size still needs, as one sentence, for the chosen product.
  function needCopy(info, entry) {
    if (!info.cards) return '';
    if (info.covered) {
      return info.cards === 1 ? 'Your ' + cardsText(1, info.size) + ' is covered.' : 'All ' + cardsText(info.cards, info.size) + ' are covered.';
    }
    var count = entry ? Math.ceil(info.missing / entry.perPack) : 0;
    if (info.sleevesHave > 0) {
      var have = 'You have ' + info.sleevesHave + ' sleeves for ' + cardsText(info.cards, info.size) + '.';
      return count ? have + ' ' + count + ' more ' + (count === 1 ? 'pack covers' : 'packs cover') + ' the rest.' : have;
    }
    return 'Your ' + cardsText(info.cards, info.size) + (info.cards === 1 ? ' needs ' : ' need ') + (count ? packs(count) + '.' : 'sleeves.');
  }

  // After an add that leaves the size short: what was added and what is left.
  // multi: the cart has decks of more than one size.
  function addedCopy(added, more, size, multi) {
    var kind = size === 'standard' ? (multi ? ' standard' : '') : ' ' + SIZE_NAME[size];
    return 'Added ' + packs(added) + '.' + (more > 0 ? ' Add ' + more + ' more ' + (more === 1 ? 'pack' : 'packs') + ' to cover all your' + kind + ' decks.' : '');
  }

  // The popup's lead: the sleeves for the decks just added.
  function popupCopy(plan, entry) {
    var deckPacks = Math.ceil(plan.deckCards / entry.perPack);
    var kind = plan.size === 'standard' ? 'standard' : SIZE_NAME[plan.size];
    var noun = planNoun(plan);
    var who;
    if (noun) who = plan.decks > 1 ? 'These ' + plan.decks + ' ' + noun + 's need ' : 'This ' + noun + ' needs ';
    else who = plan.decks === 2 ? 'This deck and set need ' : 'These decks and sets need ';
    var lead = who + packs(deckPacks) + ' of ' + kind + ' sleeves.';
    return { lead: lead };
  }

  function metaCopy(entry, variant, moneyFormat, showColor) {
    var parts = [];
    if (showColor && variant && variant.label) parts.push(variant.label);
    parts.push(entry.perPack + ' sleeves per pack');
    if (variant) parts.push(formatMoney(variant.price, moneyFormat));
    return parts.join(' · ');
  }

  // The add button's label, on the cart card and in the popup: the packs and
  // their total ("Add 3 packs, $38.91"). The pack price is on the product line.
  function addCopy(count, cents, moneyFormat) {
    return 'Add ' + packs(count) + ', ' + formatMoney(cents * count, moneyFormat);
  }

  // The same total for a screen reader, after a pack count change.
  function totalNotice(count, cents, moneyFormat) {
    return formatMoney(cents * count, moneyFormat) + ' for ' + packs(count) + '.';
  }

  // The cart card's add button: "Add 3 packs to cart" (the total is beside it).
  function cardAddCopy(count) {
    return 'Add ' + packs(count) + ' to cart';
  }

  // ---------------------------------------------------- page state, events

  // choice: the brand and color picked per size on this page.
  // sent: the last add a product form sent (see onFormSubmit).
  // edits: packs the shopper set on the card per size, kept across re-renders
  // while the recommendation is the same. justAdded: the card add that just
  // landed, so the re-rendered card shows "Added" (see renderDone).
  // dismissed: "No thanks" (true) or "Undo" (false) on this page; null: as
  // sessionStorage says (it keeps "No thanks" for the visit when it can).
  var state = { dismissed: null, pending: null, shown: {}, choice: {}, popup: null, inFlight: false, restoreFocus: null, hold: null, sent: null, edits: {}, justAdded: null };

  function justAddedFor(placement) {
    var done = state.justAdded;
    if (!done || done.placement !== placement) return null;
    if (Date.now() - done.at >= Math.max(TIMING.added, TIMING.confirm)) { state.justAdded = null; return null; }
    return done;
  }

  function storage() {
    try { return window.sessionStorage || null; } catch (error) { return null; }
  }

  // "No thanks" lasts for the visit: sessionStorage, or this page view when
  // storage is blocked (private modes, strict settings, some in-app browsers).
  function offerDismissed() {
    if (state.dismissed !== null) return state.dismissed;
    try {
      var store = storage();
      return Boolean(store && store.getItem(DISMISS_KEY) === '1');
    } catch (error) {
      return false;
    }
  }

  function dismissOffer() {
    state.dismissed = true;
    try {
      var store = storage();
      if (store) store.setItem(DISMISS_KEY, '1');
    } catch (error) { /* storage blocked: hidden until the page reloads */ }
  }

  // "Undo": the card is back, for this page and the rest of the visit.
  function restoreOffer() {
    state.dismissed = false;
    try {
      var store = storage();
      if (store) store.removeItem(DISMISS_KEY);
    } catch (error) { /* storage blocked: shown for this page */ }
  }

  function publishAnalytics(name, payload) {
    try {
      var shopify = window.Shopify;
      if (shopify && shopify.analytics && typeof shopify.analytics.publish === 'function') shopify.analytics.publish(name, payload);
    } catch (error) { /* analytics never blocks the cart */ }
  }

  // The lines an add put in the cart ({key, variant_id}), from the /cart/add.js answer: one item or {items}.
  function addedItems(detail) {
    var added = detail && detail.product;
    if (!added || typeof added !== 'object') return [];
    var items = Array.isArray(added.items) ? added.items : [added];
    return items.filter(function (item) { return item && (item.key != null || item.variant_id != null); });
  }

  function routes() {
    return (window.FoxTheme && window.FoxTheme.routes) || {};
  }

  function groupedSections() {
    var sections = [];
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sections },
      }));
    } catch (error) { /* no sections: the cart is re-read instead */ }
    return sections.filter(function (id, index) { return id && sections.indexOf(id) === index; });
  }

  // fetch with a timeout that covers the body too: resolves {ok, status,
  // body} (body: the parsed JSON, or null) once the whole answer is read.
  // Rejects with {timeout: true} or the network error.
  function timedFetch(url, init, timeout) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var options = Object.assign({}, init || {});
    if (controller) options.signal = controller.signal;
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) { try { controller.abort(); } catch (error) { /* already done */ } }
        reject({ timeout: true });
      }, timeout || TIMEOUTS.fetch);
      Promise.resolve().then(function () { return fetch(url, options); }).then(function (response) {
        if (settled) return null;
        var read = response && typeof response.json === 'function' ? Promise.resolve().then(function () { return response.json(); }) : Promise.resolve(null);
        return read.then(function (body) { return body; }, function () { return null; }).then(function (body) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve({ ok: Boolean(response.ok), status: response.status, body: body == null ? null : body });
        });
      }).then(null, function (error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        reject(error || new Error('fetch'));
      });
    });
  }


  async function fetchCart() {
    var response = await timedFetch((routes().cart_url || '/cart') + '.js', { headers: { Accept: 'application/json' } });
    if (!response.ok || !response.body) throw new Error('cart');
    return response.body;
  }

  function publishCart(cart) {
    var theme = window.FoxTheme;
    if (theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    }
  }

  async function fetchSections(sections, timeout) {
    var response = await timedFetch(window.location.pathname + '?sections=' + encodeURIComponent(sections.join(',')), {
      headers: { Accept: 'application/json' },
    }, timeout);
    if (!response.ok || !response.body) throw new Error('sections');
    return response.body;
  }

  // Reloads the cart and its sections so the card shows what the store holds now.
  async function refreshCart(sections) {
    var cart = await fetchCart();
    if (sections.length) {
      try { cart.sections = await fetchSections(sections); } catch (error) { /* the cart alone still updates the counts */ }
    }
    publishCart(cart);
  }

  // One cart request: 'add' {id, quantity} or 'change' {id: line key, quantity}.
  // Never line item properties. landed() runs once the store has taken the
  // request, before the cart re-renders. {ok, result, unanswered, sections}.
  async function cartRequest(kind, id, quantity, landed) {
    var sections = groupedSections();
    var body = { id: id, quantity: quantity };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    var url = kind === 'change' ? (routes().cart_change_url || '/cart/change') + '.js' : (routes().cart_add_url || '/cart/add') + '.js';
    var result;
    try {
      var response = await timedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      result = response.body && typeof response.body === 'object' ? response.body : {};
      if (!response.ok || result.status || result.errors) return { ok: false, sections: sections, result: result };
    } catch (error) {
      return { ok: false, unanswered: true, sections: sections };
    }
    if (landed) { try { landed(); } catch (error) { /* the cart still re-renders */ } }
    // change.js answers with the cart; add.js with the item, so the cart is re-read.
    var cart = null;
    if (kind === 'change' && Array.isArray(result.items)) cart = result;
    else {
      try { cart = await fetchCart(); } catch (error) { cart = null; }
    }
    if (cart) {
      cart.sections = result.sections || {};
      publishCart(cart);
    } else if (sections.length) {
      try { await refreshCart(sections); } catch (error) { /* the change landed; the card says so */ }
    }
    return { ok: true, sections: sections, result: result };
  }

  // After a refused or unanswered request: reload so the card cannot act twice on stale numbers.
  async function recover(outcome) {
    try { await refreshCart(outcome.sections || []); return true; } catch (error) { return false; }
  }

  // ------------------------------------------------------------ UI builders

  var uid = 0;
  function nextId(prefix) { uid += 1; return prefix + '-' + uid; }

  function el(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content != null) node.textContent = content;
    return node;
  }

  var SVG_MINUS = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_PLUS = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.6875 7H12.3125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.5 2.1875V11.8125" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_CLOSE = '<svg aria-hidden="true" focusable="false" class="icon icon-close icon--large" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SVG_CHECK = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 8.5L6.5 11.5L12.5 4.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function spinner() {
    var wrap = el('div', 'loading__spinner hidden');
    wrap.innerHTML = '<svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle></svg>';
    return wrap;
  }

  function button(className, label) {
    var node = el('button', className);
    node.type = 'button';
    var span = el('span', null, label);
    node.appendChild(span);
    node.appendChild(spinner());
    node._label = span;
    return node;
  }

  function setLabel(node, label) {
    if (node && node._label) node._label.textContent = label;
    else if (node) node.textContent = label;
  }

  function thumb(source) {
    var img = el('img');
    img.alt = '';
    img.width = 120;
    img.height = 120;
    img.decoding = 'async';
    applySource(img, source);
    return img;
  }

  // The product thumbnail, `shown` CSS px wide. previous: the image this one
  // replaces (a brand or color switch): it stays until the new one is
  // decoded, so the box never flashes empty. The box has a fixed size, so
  // nothing moves.
  function media(src, shown, previous) {
    var wrap = el('div', 'trc-sleeve-media');
    var source = imageSource(src, shown);
    if (!source) return wrap;
    var img = thumb(source);
    var before = previous && previous !== src ? imageSource(previous, shown) : null;
    if (!before || decodedImages[source.key] || img.complete || typeof img.decode !== 'function') {
      if (!before) img.loading = 'lazy';
      wrap.appendChild(img);
      return wrap;
    }
    var under = thumb(before);
    under.className = 'trc-sleeve-media__under';
    under.setAttribute('aria-hidden', 'true');
    img.className = 'is-loading';
    wrap.appendChild(under);
    wrap.appendChild(img);
    function reveal() {
      img.className = '';
      if (under.parentNode) under.parentNode.removeChild(under);
    }
    img.decode().then(function () { decodedImages[source.key] = img; reveal(); }, reveal);
    return wrap;
  }

  // The theme's quantity selector: minus, a number field, plus. onChange(value).
  function quantityControl(options) {
    var min = options.min;
    var max = Math.max(min, Number.isFinite(options.max) ? options.max : MAX_PACKS);
    var wrap = el('div', 'quantity trc-sleeve-qty');
    var minus = el('button', 'quantity__button');
    minus.type = 'button';
    minus.name = 'minus';
    minus.setAttribute('aria-label', 'One pack fewer');
    minus.innerHTML = SVG_MINUS;
    var input = el('input', 'quantity__input');
    input.type = 'number';
    input.inputMode = 'numeric';
    input.min = String(min);
    input.max = String(max);
    input.step = '1';
    input.autocomplete = 'off';
    input.setAttribute('aria-label', options.label || 'Packs');
    if (options.describedBy) input.setAttribute('aria-describedby', options.describedBy);
    var plus = el('button', 'quantity__button');
    plus.type = 'button';
    plus.name = 'plus';
    plus.setAttribute('aria-label', 'One pack more');
    plus.innerHTML = SVG_PLUS;
    wrap.appendChild(minus);
    wrap.appendChild(input);
    wrap.appendChild(plus);

    var value = clamp(options.value);
    function clamp(next) {
      var number = Math.floor(Number(next));
      if (!Number.isFinite(number)) number = value != null ? value : min;
      return Math.min(max, Math.max(min, number));
    }
    function set(next, silent) {
      value = clamp(next);
      input.value = String(value);
      minus.disabled = value <= min;
      plus.disabled = value >= max;
      if (!silent && options.onChange) options.onChange(value);
    }
    minus.addEventListener('click', function () { set(value - 1); });
    plus.addEventListener('click', function () { set(value + 1); });
    input.addEventListener('change', function (event) { event.stopPropagation(); set(input.value); });
    input.addEventListener('input', function (event) {
      event.stopPropagation();
      if (input.value === '') return;
      var number = Number(input.value);
      if (Number.isFinite(number) && number >= min && number <= max) { value = Math.floor(number); minus.disabled = value <= min; plus.disabled = value >= max; if (options.onChange) options.onChange(value); }
    });
    set(value, true);
    wrap.trcGet = function () { return value; };
    wrap.trcSet = function (next) { set(next, true); };
    wrap.trcLimit = function (nextMax) { max = Math.max(min, Number.isFinite(nextMax) ? nextMax : MAX_PACKS); input.max = String(max); set(value, true); };
    wrap.trcInput = input;
    return wrap;
  }

  function radioInput(name, value, open, checked) {
    var radio = el('input', 'trc-sleeve-pill__input');
    radio.type = 'radio';
    radio.name = name;
    radio.id = nextId(name + '-');
    radio.value = value;
    if (!open) { radio.disabled = true; radio.className += ' disabled'; }
    if (checked) radio.checked = true;
    return radio;
  }

  // The fieldset legend: the field name and, for colors, the chosen one in
  // words (the swatches carry no visible text).
  function legendFor(name, value) {
    var legend = el('legend', 'trc-sleeve-field__label', name);
    if (value) {
      legend.appendChild(document.createTextNode(': '));
      legend.appendChild(el('span', 'trc-sleeve-field__value', value));
    }
    return legend;
  }

  // Brand buttons (only with more than one product of the size: the name and
  // the pack price) and color swatches (only with more than one color; text
  // pills when a color has no swatch or name fill). Sold out choices are
  // disabled. onPick({productId, variantId}).
  function chooser(info, current, inCart, onPick, moneyFormat) {
    var wrap = el('div', 'trc-sleeve-choose');
    var products = info.products;
    if (products.length > 1) {
      var brandSet = el('fieldset', 'trc-sleeve-field trc-sleeve-brands');
      brandSet.setAttribute('data-trc-group', 'brand');
      brandSet.appendChild(legendFor('Brand'));
      var brandGroup = el('div', 'trc-sleeve-pills trc-sleeve-pills--brands');
      var brandName = nextId('TrcSleeveBrand');
      var brands = {};
      products.forEach(function (entry) { brands[lower(entry.brand)] = (brands[lower(entry.brand)] || 0) + 1; });
      products.forEach(function (entry) {
        var stock = firstSelectable(entry, inCart);
        var radio = radioInput(brandName, text(entry.productId), Boolean(stock), text(entry.productId) === text(current.entry.productId));
        radio.addEventListener('change', function (event) {
          event.stopPropagation();
          if (!radio.checked) return;
          var variant = firstSelectable(entry, inCart) || entry.variants[0];
          onPick({ productId: entry.productId, variantId: variant.id });
        });
        var pill = el('label', 'trc-sleeve-pill trc-sleeve-pill--brand');
        pill.htmlFor = radio.id;
        pill.appendChild(el('span', 'trc-sleeve-pill__name', brands[lower(entry.brand)] > 1 ? entry.title : entry.brand));
        pill.appendChild(document.createTextNode(' '));
        pill.appendChild(el('span', 'trc-sleeve-pill__detail', stock ? entry.perPack + ' pack · ' + formatMoney(stock.price, moneyFormat) : 'Sold out'));
        brandGroup.appendChild(radio);
        brandGroup.appendChild(pill);
      });
      brandSet.appendChild(brandGroup);
      wrap.appendChild(brandSet);
    }
    var variants = current.entry.variants;
    if (variants.length > 1) {
      var isColor = current.entry.colorIndex !== -1;
      var swatched = isColor && variants.every(function (variant) { return variant.swatch; });
      var set = el('fieldset', 'trc-sleeve-field trc-sleeve-colors');
      set.setAttribute('data-trc-group', 'color');
      set.appendChild(legendFor(isColor ? 'Color' : 'Option', swatched ? current.variant.label : ''));
      var group = el('div', swatched ? 'trc-sleeve-swatches' : 'trc-sleeve-pills');
      var name = nextId('TrcSleeveColor');
      variants.forEach(function (variant) {
        var open = selectable(variant, inCart);
        var radio = radioInput(name, text(variant.id), open, text(variant.id) === text(current.variant.id));
        radio.addEventListener('change', function (event) {
          event.stopPropagation();
          if (radio.checked) onPick({ productId: current.entry.productId, variantId: variant.id });
        });
        var pill;
        if (swatched) {
          pill = el('label', 'trc-sleeve-swatch' + (variant.swatch.light ? ' trc-sleeve-swatch--light' : ''));
          pill.title = variant.label + (open ? '' : ', sold out');
          var chip = el('span', 'trc-sleeve-swatch__chip');
          chip.setAttribute('aria-hidden', 'true');
          if (variant.swatch.image) chip.style.backgroundImage = 'url("' + variant.swatch.image.replace(/["\\\n\r]/g, '') + '")';
          else chip.style.background = variant.swatch.fill;
          pill.appendChild(chip);
          pill.appendChild(el('span', 'visually-hidden', variant.label));
        } else {
          pill = el('label', 'trc-sleeve-pill', variant.label || 'Option');
        }
        pill.htmlFor = radio.id;
        if (!open) pill.appendChild(el('span', 'visually-hidden', ', sold out'));
        group.appendChild(radio);
        group.appendChild(pill);
      });
      set.appendChild(group);
      wrap.appendChild(set);
    }
    return wrap.childNodes.length ? wrap : null;
  }

  // Focus without scrolling the page (Safari 14 and Chrome take the option).
  function focusQuietly(node) {
    try { node.focus({ preventScroll: true }); } catch (error) { try { node.focus(); } catch (ignore) { /* not focusable */ } }
  }

  // Which chooser group (brand, color) holds focus, to put it back after a rebuild.
  function focusedGroup(container) {
    var active = document.activeElement;
    var group = active && container.contains(active) && active.closest ? active.closest('[data-trc-group]') : null;
    return group ? group.getAttribute('data-trc-group') : '';
  }

  function refocusGroup(container, group) {
    var again = group && container ? container.querySelector('[data-trc-group="' + group + '"] input[type="radio"]:checked') : null;
    if (again) focusQuietly(again);
  }

  // shown: the thumbnail's CSS width; previous: the image it replaces.
  function productBlock(entry, variant, moneyFormat, linked, shown, previous) {
    var row = el('div', 'trc-sleeve-product');
    row.appendChild(media(variant.image || entry.image, shown || 56, previous));
    var info = el('div', 'trc-sleeve-product__info');
    var title;
    if (linked && entry.url) {
      title = el('a', 'trc-sleeve-product__title reversed-link', entry.title);
      title.href = entry.url;
    } else {
      title = el('p', 'trc-sleeve-product__title', entry.title);
    }
    info.appendChild(title);
    info.appendChild(el('p', 'trc-sleeve-product__meta', metaCopy(entry, variant, moneyFormat, entry.variants.length > 1)));
    row.appendChild(info);
    return row;
  }

  // ---------------------------------------------------------------- the card

  // Where focus goes when the card closes under it: the cart's checkout
  // button, else its subtotal (drawer or cart page), without scrolling.
  function focusAfterCard(card) {
    var scope = (card.closest && (card.closest('cart-drawer') || card.closest('main-cart'))) || document;
    var targets = [scope.querySelector('button[name="checkout"]'), document.querySelector('button[name="checkout"]'),
      scope.querySelector('.totals__subtotal-value'), document.querySelector('.totals__subtotal-value')];
    for (var index = 0; index < targets.length; index += 1) {
      var target = targets[index];
      if (!target || target.disabled || (target.closest && target.closest('[hidden]'))) continue;
      if (target.tagName !== 'BUTTON' && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      focusQuietly(target);
      if (document.activeElement === target) return;
    }
  }

  // "Added" with a check mark (put in first); a screen reader hears "Added
  // sleeves to your cart".
  function appendAdded(line) {
    line.appendChild(el('span', 'trc-sleeve-offer__done-text', COPY.added));
    line.appendChild(el('span', 'visually-hidden', ' sleeves to your cart'));
  }

  class TrcSleeveOffer extends HTMLElement {
    connectedCallback() {
      this.watchInteraction();
      try { this.render(); } catch (error) { this.hidden = true; }
    }

    // The "Added 4 packs..." line stays until the shopper does something in a
    // size group (capture: the controls stop their own events).
    watchInteraction() {
      if (this._watching) return;
      this._watching = true;
      var self = this;
      function onInteract(event) {
        if (self._confirm && event.target && event.target.closest && event.target.closest('.trc-sleeve-offer__size')) self.endConfirm(false);
      }
      this.addEventListener('click', onInteract, true);
      this.addEventListener('input', onInteract, true);
      this.addEventListener('change', onInteract, true);
    }

    get placement() {
      return this.dataset.placement === 'page' ? 'page' : 'drawer';
    }

    readData() {
      if (this._data !== undefined) return this._data;
      var source = this.querySelector('script[data-trc-sleeve-data]');
      try { this._data = source ? JSON.parse(source.textContent) : null; } catch (error) { this._data = null; }
      return this._data;
    }

    // The card, its rows and its live region. The snippet prints all three so
    // the status region is in the page before any message is set; only the
    // rows are rebuilt on each render.
    parts() {
      var card = this.querySelector('.trc-sleeve-offer__card');
      if (!card) {
        card = el('div', 'trc-sleeve-offer__card');
        this.appendChild(card);
      }
      var rows = card.querySelector('[data-trc-sleeve-rows]');
      if (!rows) {
        rows = el('div');
        rows.setAttribute('data-trc-sleeve-rows', '');
        card.insertBefore(rows, card.firstChild);
      }
      var status = card.querySelector('[data-trc-sleeve-status]');
      if (!status) {
        status = el('p', 'trc-sleeve-offer__status');
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('data-trc-sleeve-status', '');
        card.appendChild(status);
      }
      return { rows: rows, status: status };
    }

    render() {
      // The snippet's empty marker (nothing to offer), and the drawer, whose
      // element only carries the cart data for the popup: stays hidden.
      if (this.placement !== 'page' || (this.hasAttribute('data-trc-sleeve-state') && !this.querySelector('script[data-trc-sleeve-data]'))) {
        this.hidden = true;
        return;
      }
      var self = this;
      var data = this.readData();
      this.stopPreload();
      this.stopCollapse();
      this.stopTimers();
      try { mergeAccountPref(data); } catch (error) { /* only a default */ }
      var result = data ? computeSizes(data) : { sizes: [], inCart: {} };
      this._result = result;
      var placement = this.placement;
      var pending = state.pending && state.pending.placement === placement ? state.pending : null;
      if (pending) state.pending = null;
      var done = justAddedFor(placement);
      var now = Date.now();

      var parts = this.parts();
      this._status = parts.status;
      parts.rows.textContent = '';
      clearTimeout(this._announceTimer);
      parts.status.textContent = '';
      parts.status.classList.remove('trc-sleeve-offer__status--quiet');
      this.classList.remove('trc-sleeve-offer--done');
      this._busy = false;
      this.removeAttribute('aria-busy');
      this._shownPayload = null;
      this.classList.remove('trc-sleeve-offer--dismissed');

      // "No thanks": one small line with "Undo", for the rest of the visit,
      // as long as there is something the card would offer.
      if (offerDismissed()) {
        if (!result.sizes.some(function (info) { return self.offerFor(info, data); })) {
          this.hidden = true;
          this.removeAttribute('role');
          this.removeAttribute('aria-labelledby');
          return;
        }
        this.renderDismissed(parts);
        return;
      }

      // Only a size still short of sleeves (or with a note) gets a group: a
      // covered size shows nothing, and with every size covered the card is hidden.
      // The size just added to: still short, its group shows what was added and
      // what is left (in place of the need sentence); covered, an "Added"
      // group for a moment, then it closes. Other sizes are as they were.
      var multi = result.sizes.filter(function (info) { return info.cards > 0; }).length > 1;
      var groups = [];
      var shown = [];
      var doneGroup = null;
      result.sizes.forEach(function (info) {
        var mine = done && done.size === info.size;
        if (mine && info.cards + ':' + info.sleevesHave !== done.expect) {
          // The cart changed since the add: no "Added..." or "Added" for it.
          done.dismissed = true;
          done.stale = true;
          mine = false;
        }
        var group = self.renderSize(info, data, mine && !done.dismissed && now - done.at < TIMING.confirm ? done : null, multi);
        if (!group && mine && !done.stale && now - done.at < TIMING.added) {
          group = doneGroup = self.renderDoneGroup(info);
        }
        if (group) { groups.push(group); if (group !== doneGroup) shown.push(info); }
      });

      // The add covered every size: "Added" in place of the card, then it closes.
      if (doneGroup && groups.length === 1 && !pending) {
        this.renderDone(parts, done);
        return;
      }

      if (!groups.length && !pending) {
        this.hidden = true;
        this.removeAttribute('role');
        this.removeAttribute('aria-labelledby');
        return;
      }

      var headingId = nextId('TrcSleeveHeading');
      parts.rows.appendChild(this.renderTop(headingId));
      this.setAttribute('aria-labelledby', headingId);
      this.setAttribute('role', 'region');
      groups.forEach(function (group) { parts.rows.appendChild(group); });
      this.hidden = false;

      if (pending) this.announce(pending.text, pending.tone);
      if (state.inFlight) this.lockButtons(true);
      if (state.restoreFocus && state.restoreFocus.placement === placement) this.restoreFocus(state.restoreFocus.size);
      // Read out once, politely (a re-render of the cart section shows it
      // again, silently, for the time left): by focus when the "Added"
      // line takes it, else through the status line.
      if (done && !done.announced && (this._confirm || doneGroup)) {
        done.announced = true;
        var line = doneGroup && doneGroup.querySelector('.trc-sleeve-offer__done');
        // The Add button is described by the line: focus on it reads it out.
        var describedAdd = this._confirm && this._confirm.node.parentNode && this._confirm.node.closest('.trc-sleeve-offer__size').querySelector('.trc-sleeve-offer__add');
        if (this._confirm) { if (document.activeElement !== describedAdd) this.announce(this._confirm.text, 'success', true); }
        else if (document.activeElement !== line) this.announce(COPY.addedSpoken, 'success', true);
      }
      if (this._confirm) {
        this._confirm.timer = window.setTimeout(function () { self.endConfirm(true); }, Math.max(0, done.at + TIMING.confirm - now));
      }
      if (doneGroup) {
        this._doneTimer = window.setTimeout(function () {
          if (!self.isConnected) return;
          if (state.justAdded === done) state.justAdded = null;
          self.collapseGroup(doneGroup);
        }, Math.max(0, done.at + TIMING.added - now));
      }
      if (shown.length) this.reportShown(shown);
      this.preloadIfVisible();
    }

    // The card's top row: "Optional add-on" and "No thanks", nothing else.
    renderTop(headingId) {
      var self = this;
      var top = el('div', 'trc-sleeve-offer__top');
      var heading = el('h3', 'trc-sleeve-offer__heading', COPY.heading);
      heading.id = headingId;
      top.appendChild(heading);
      var dismiss = el('button', 'trc-sleeve-offer__dismiss', COPY.dismiss);
      dismiss.type = 'button';
      dismiss.addEventListener('click', function () {
        dismissOffer();
        publishAnalytics('trc_sleeve_offer_dismissed', { placement: self.placement });
        self.render();
        // Focus on Undo, so it can be reversed at once; the status line reads it out.
        var undo = self.querySelector('.trc-sleeve-offer__undo');
        if (undo) {
          focusQuietly(undo);
          self.announce(COPY.hidden, null, true);
        } else {
          focusAfterCard(self);
        }
      });
      top.appendChild(dismiss);
      return top;
    }

    // After "No thanks": "Sleeve suggestion hidden." and "Undo", in the same
    // dashed box. Undo brings the card back as it was (the brand, color and
    // packs picked on this page are kept) with focus on "No thanks".
    renderDismissed(parts) {
      var self = this;
      this.classList.add('trc-sleeve-offer--dismissed');
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      var row = el('div', 'trc-sleeve-offer__hidden-row');
      row.appendChild(el('p', 'trc-sleeve-offer__hidden-text', COPY.hidden));
      var undo = el('button', 'trc-sleeve-offer__undo', COPY.undo);
      undo.type = 'button';
      undo.addEventListener('click', function () {
        restoreOffer();
        publishAnalytics('trc_sleeve_offer_undo', { placement: self.placement });
        self.render();
        var back = self.querySelector('.trc-sleeve-offer__dismiss');
        if (back) focusQuietly(back);
        else focusAfterCard(self);
      });
      row.appendChild(undo);
      parts.rows.appendChild(row);
      this.hidden = false;
    }

    stopTimers() {
      window.clearTimeout(this._doneTimer);
      if (this._confirm) {
        window.clearTimeout(this._confirm.timer);
        window.clearTimeout(this._confirm.fadeTimer);
      }
      this._confirm = null;
    }

    // The confirmation line goes back to the need sentence: faded at the end of
    // its time, at once when the shopper acts on the card.
    endConfirm(fade) {
      var confirm = this._confirm;
      if (!confirm) return;
      this._confirm = null;
      window.clearTimeout(confirm.timer);
      confirm.done.dismissed = true;
      if (state.justAdded === confirm.done) state.justAdded = null;
      if (this._status && this._status.classList.contains('trc-sleeve-offer__status--quiet')) this._status.textContent = '';
      var node = confirm.node;
      function swap() {
        node.classList.remove('trc-sleeve-offer__need--added', 'is-fading');
        node.textContent = confirm.normal;
      }
      if (!fade || reducedMotion()) { swap(); return; }
      node.classList.add('is-fading');
      confirm.fadeTimer = window.setTimeout(swap, TIMING.fade);
    }

    // A covered size just added to, beside sizes still short: its title and
    // "Added" for a moment, then the group closes (collapseGroup).
    renderDoneGroup(info) {
      var group = el('div', 'trc-sleeve-offer__size trc-sleeve-offer__size--' + info.size + ' trc-sleeve-offer__size--done');
      group.setAttribute('data-size', info.size);
      var head = el('div', 'trc-sleeve-offer__size-head');
      head.appendChild(el('p', 'trc-sleeve-offer__size-title', sizeTitle(info.size)));
      group.appendChild(head);
      var line = el('p', 'trc-sleeve-offer__done');
      line.tabIndex = -1;
      line.insertAdjacentHTML('afterbegin', SVG_CHECK);
      appendAdded(line);
      group.appendChild(line);
      return group;
    }

    // Closes one size group (height, its top spacing, fade; at once with reduced
    // motion). When it is the first group, the next one loses its top border
    // and spacing at the same time, so nothing jumps when it is removed.
    collapseGroup(group) {
      var self = this;
      if (!group.parentNode) return;
      var isGroup = function (node) { return node && node.classList && node.classList.contains('trc-sleeve-offer__size'); };
      var next = isGroup(group.previousElementSibling) ? null : (isGroup(group.nextElementSibling) ? group.nextElementSibling : null);
      var timer = null;
      function flatten(node) {
        node.style.marginTop = '0px';
        node.style.paddingTop = '0px';
        node.style.borderTopWidth = '0px';
      }
      function end() {
        window.clearTimeout(timer);
        var hadFocus = group.contains(document.activeElement);
        if (group.parentNode) group.parentNode.removeChild(group);
        if (next) next.classList.remove('is-settling');
        if (hadFocus) self.restoreFocus(null, true);
      }
      if (reducedMotion() || !group.offsetHeight) { end(); return; }
      group.style.height = group.offsetHeight + 'px';
      void group.offsetHeight;
      group.classList.add('is-collapsing');
      group.style.height = '0px';
      group.style.opacity = '0';
      flatten(group);
      if (next) { next.classList.add('is-settling'); flatten(next); }
      timer = window.setTimeout(end, TIMING.collapse);
    }

    // The add covered every size: "Added" in place of the offer, read
    // out once (a re-render of the cart section shows it again, silently, for
    // the time left). Then the card closes.
    renderDone(parts, done) {
      var self = this;
      this.classList.add('trc-sleeve-offer--done');
      this.removeAttribute('role');
      this.removeAttribute('aria-labelledby');
      var line = el('p', 'trc-sleeve-offer__done');
      line.tabIndex = -1;
      line.insertAdjacentHTML('afterbegin', SVG_CHECK);
      appendAdded(line);
      parts.rows.appendChild(line);
      this.hidden = false;
      // Read out once: by focus when "Added" takes it (the shopper was
      // on the add button), else through the status line.
      if (state.restoreFocus && state.restoreFocus.placement === this.placement) this.restoreFocus(null);
      if (!done.announced) {
        done.announced = true;
        if (document.activeElement !== line) this.announce(COPY.addedSpoken, 'success', true);
      }
      this._doneTimer = window.setTimeout(function () {
        if (!self.isConnected) return;
        if (state.justAdded === done) state.justAdded = null;
        self.collapse();
      }, Math.max(0, done.at + TIMING.added - Date.now()));
    }

    // Closes the card: a short height and fade, or at once with reduced motion.
    collapse() {
      var self = this;
      this.stopCollapse();
      function end() {
        var hadFocus = self.contains(document.activeElement);
        self.stopCollapse();
        self.hidden = true;
        if (hadFocus) focusAfterCard(self);
      }
      if (reducedMotion() || !this.offsetHeight) { end(); return; }
      this.style.height = this.offsetHeight + 'px';
      void this.offsetHeight;
      this.classList.add('is-collapsing');
      this.style.height = '0px';
      this.style.opacity = '0';
      function onEnd(event) { if (event.target === self && event.propertyName === 'height') end(); }
      this.addEventListener('transitionend', onEnd);
      this._collapse = { onEnd: onEnd, timer: window.setTimeout(end, TIMING.collapse) };
    }

    stopCollapse() {
      var collapse = this._collapse;
      if (!collapse) return;
      this._collapse = null;
      window.clearTimeout(collapse.timer);
      this.removeEventListener('transitionend', collapse.onEnd);
      this.classList.remove('is-collapsing');
      this.style.height = '';
      this.style.opacity = '';
    }

    // An add the cart did not re-render this card for: count it in the card's
    // own copy of the cart, so what shows next is right.
    addLocal(meta) {
      var data = this.readData();
      if (!data) return;
      if (!Array.isArray(data.lines)) data.lines = [];
      var line = data.lines.filter(function (item) { return item && text(item.variantId) === text(meta.variantId); })[0];
      if (line) line.quantity = quantityOf(line.quantity) + meta.packs;
      else data.lines.push({ key: 'trc-added-' + meta.variantId, variantId: meta.variantId, productId: meta.productId, quantity: meta.packs, type: data.sleeveType || 'Card Sleeves', tags: [] });
    }

    disconnectedCallback() {
      this.stopPreload();
      this.stopCollapse();
      this.stopTimers();
    }

    stopPreload() {
      if (this._preload) this._preload.cancel();
      this._preload = null;
    }

    // Once the card shows, the thumbnails a brand or color switch would show
    // load in the background, so the switch is instant.
    preloadIfVisible() {
      if (this._preload || this.hidden || !this.isConnected || !this._result || !this._result.sizes.length) return;
      if (!this.querySelector('.trc-sleeve-offer__item--suggest')) return;
      var data = this.readData();
      var inCart = this._result.inCart;
      var sources = [];
      this._result.sizes.forEach(function (info) {
        if (!(info.missing > 0)) return;
        sources = sources.concat(switchImages(info, resolveChoice(info, inCart, choiceFor(info, data)), inCart, 56));
      });
      if (!sources.length) return;
      this._preload = preloader();
      this._preload.add(sources);
    }

    // One size: what it still needs, then the offer to add the packs missing.
    // Sleeves already in the cart only count toward the need: their quantity
    // is changed on the cart's own lines, never here. null: nothing to show.
    // done: the add just made to this size, when it is still short: the need
    // sentence shows what was added and what is left instead (see endConfirm).
    // What one size offers: {short, open, notes, current}, or null when the
    // size shows nothing (covered, or short with nothing left to sell and no note).
    offerFor(info, data) {
      var inCart = this._result.inCart;
      var short = info.missing > 0;
      var notes = [];
      if (info.unknown) notes.push(COPY.unknown);
      if (info.odd) notes.push(COPY.oddSize);
      if (!short && !notes.length) return null;
      var current = resolveChoice(info, inCart, choiceFor(info, data));
      // Only counted cards get a one-click add: decks we could not count, or of
      // another size (tarot, square), get the check note alone.
      var open = Boolean(short && current && selectable(current.variant, inCart));
      // Short, but every sleeve of this size is sold out (or in the cart up to
      // the stock): nothing to offer, so no group (unless a note has to show).
      if (!open && !notes.length) return null;
      return { short: short, open: open, notes: notes, current: current };
    }

    renderSize(info, data, done, multi) {
      var inCart = this._result.inCart;
      var offer = this.offerFor(info, data);
      if (!offer) return null;
      var short = offer.short;
      var notes = offer.notes;
      var current = offer.current;
      var open = offer.open;
      var group = el('div', 'trc-sleeve-offer__size trc-sleeve-offer__size--' + info.size);
      group.setAttribute('data-size', info.size);
      var head = el('div', 'trc-sleeve-offer__size-head');
      head.appendChild(el('p', 'trc-sleeve-offer__size-title', sizeTitle(info.size)));
      var need = el('p', 'trc-sleeve-offer__need');
      need.id = nextId('TrcSleeveNeed');
      var normal = short ? needCopy(info, current && current.entry) : '';
      // With too few packs left to cover the rest, no "Add N more" (the stepper
      // is capped and the "Only N left." note says why).
      var plan = open ? recommend(info, current.entry, current.variant, inCart) : null;
      var confirm = done && open ? addedCopy(done.packs, plan.partial ? 0 : plan.packs, info.size, multi) : '';
      if (confirm) {
        need.className += ' trc-sleeve-offer__need--added';
        need.insertAdjacentHTML('afterbegin', SVG_CHECK);
        need.appendChild(document.createTextNode(confirm));
        this._confirm = { node: need, normal: normal, text: confirm, done: done, timer: null, fadeTimer: null };
      } else if (normal) {
        need.appendChild(document.createTextNode(normal));
      }
      if (need.textContent) head.appendChild(need);
      group.appendChild(head);

      var describedBy = need.textContent ? need.id : '';
      if (open) group.appendChild(this.renderSuggestion(info, current, data, describedBy));
      if (notes.length) {
        var note = el('p', 'trc-sleeve-offer__note', notes.join(' '));
        // Nothing to add here: a link to the sleeves, to choose by hand.
        // Another size: all the sleeves, to pick the right fit. Uncounted
        // decks: the default sleeves of this size.
        var collectionUrl = text(data.sleeveCollectionUrl);
        var linkTo = info.odd && collectionUrl ? { href: collectionUrl, label: COPY.allSleeves } : (current && current.entry.url ? { href: current.entry.url, label: current.entry.title } : null);
        if (!open && linkTo) {
          var link = el('a', 'trc-sleeve-offer__link', linkTo.label);
          link.href = linkTo.href;
          note.appendChild(document.createTextNode(' '));
          note.appendChild(link);
        }
        group.appendChild(note);
      }
      return group;
    }

    renderSuggestion(info, current, data, describedBy) {
      var self = this;
      var inCart = this._result.inCart;
      var row = el('div', 'trc-sleeve-offer__item trc-sleeve-offer__item--suggest');
      var shownMedia = this._shownMedia || (this._shownMedia = {});
      var src = current.variant.image || current.entry.image;
      row.appendChild(productBlock(current.entry, current.variant, data.moneyFormat, true, 56, shownMedia[info.size]));
      shownMedia[info.size] = src;
      var picks = chooser(info, current, inCart, function (pick) {
        var focusName = focusedGroup(row);
        state.choice[info.size] = pick;
        var chosen = resolveChoice(info, inCart, pick);
        if (chosen) rememberChoice(info.size, chosen.entry, chosen.variant, data);
        self.render();
        // The card was rebuilt: keep focus on the control that changed.
        refocusGroup(self.querySelector('.trc-sleeve-offer__size[data-size="' + info.size + '"]'), focusName);
      }, data.moneyFormat);
      if (picks) row.appendChild(picks);
      // The packs to add: the packs still missing, or what the shopper set,
      // as long as the brand and the packs missing are the same.
      var plan = recommend(info, current.entry, current.variant, inCart);
      var start = plan.offer > 0 ? plan.offer : 1;
      var edit = state.edits[info.size];
      if (edit && text(edit.productId) === text(current.entry.productId) && edit.offer === plan.offer) start = edit.value;
      else delete state.edits[info.size];
      var controls = el('div', 'trc-sleeve-offer__controls');
      var action = button('btn btn--primary trc-sleeve-dark btn--small trc-sleeve-offer__add', '');
      var total = el('p', 'trc-sleeve-offer__total');
      total.id = nextId('TrcSleeveTotal');
      action.setAttribute('aria-describedby', (describedBy ? describedBy + ' ' : '') + total.id);
      function label(value) {
        setLabel(action, cardAddCopy(value));
        total.textContent = 'Total ' + formatMoney(current.variant.price * value, data.moneyFormat);
      }
      var qty = quantityControl({
        min: 1,
        max: plan.stockLeft,
        value: start,
        label: 'Packs of ' + current.entry.title + ' to add',
        onChange: function (value) {
          label(value);
          state.edits[info.size] = { productId: current.entry.productId, offer: plan.offer, value: value };
        },
      });
      label(start);
      action.addEventListener('click', function () {
        var value = qty.trcGet();
        self.act(action, function (landed) { return cartRequest('add', current.variant.id, value, landed); }, {
          info: info, size: info.size, packs: value, before: inCart[text(current.variant.id)] || 0, recommended: plan.packs,
          variantId: current.variant.id, productId: current.entry.productId, remember: current,
        });
      });
      controls.appendChild(qty);
      controls.appendChild(total);
      controls.appendChild(action);
      row.appendChild(controls);
      if (plan.partial) row.appendChild(el('p', 'trc-sleeve-offer__note', 'Only ' + packs(plan.stockLeft) + ' left.'));
      return row;
    }

    // trc_sleeve_offer_shown is sent when the card shows on the cart page,
    // once per offer.
    reportShown(sizes) {
      var inCart = this._result.inCart;
      var data = this.readData();
      this._shownPayload = {
        placement: this.placement,
        position: 'cart',
        offers: sizes.map(function (info) {
          var current = resolveChoice(info, inCart, choiceFor(info, data));
          var plan = current ? recommend(info, current.entry, current.variant, inCart) : { offer: 0, packs: 0, partial: false };
          return { size: info.size, packs: plan.offer, cards: info.cards, needed: plan.packs, in_cart: info.sleeveLines.length, unknown: info.unknown > 0, partial: plan.partial, covered: info.covered };
        }),
      };
      this.reportIfVisible();
    }

    reportIfVisible() {
      var payload = this._shownPayload;
      if (!payload || this.hidden || !this.isConnected) return;
      this._shownPayload = null;
      var key = JSON.stringify(payload);
      if (state.shown[key]) return;
      state.shown[key] = true;
      publishAnalytics('trc_sleeve_offer_shown', payload);
    }

    // Live region text is set a moment after the region is in the page, so it is announced.
    // quiet: the message is already on screen, so the status line only reads it out.
    announce(message, tone, quiet) {
      var status = this._status;
      if (!status) return;
      status.textContent = '';
      status.classList.toggle('trc-sleeve-offer__status--error', tone === 'error');
      status.classList.toggle('trc-sleeve-offer__status--success', tone === 'success');
      status.classList.toggle('trc-sleeve-offer__status--quiet', Boolean(quiet));
      clearTimeout(this._announceTimer);
      this._announceTimer = window.setTimeout(function () { status.textContent = message; }, 50);
    }

    setBusy(node, busy) {
      this._busy = busy;
      this.toggleAttribute('aria-busy', busy);
      if (busy) node.setAttribute('aria-disabled', 'true');
      else node.removeAttribute('aria-disabled');
      node.classList.toggle('btn--loading', busy);
    }

    // Runs one cart request from a button. The cart re-renders this card on
    // success; the message is carried across that re-render.
    // While any sleeve request is out (this card, the other card or the
    // popup), every sleeve button is locked, also after a re-render.
    lockButtons(locked) {
      Array.prototype.forEach.call(this.querySelectorAll('.trc-sleeve-offer__add'), function (node) {
        if (locked) {
          if (node.getAttribute('aria-disabled') === 'true') return;
          node.setAttribute('aria-disabled', 'true');
          node.setAttribute('data-trc-locked', '');
        } else if (node.hasAttribute('data-trc-locked')) {
          node.removeAttribute('data-trc-locked');
          node.removeAttribute('aria-disabled');
        }
      });
    }

    // After a cart change re-renders the card, focus would fall to the page:
    // put it on the size group that changed (or the card heading).
    // After a re-render took focus away: the size's add button (an add that
    // left it short keeps the shopper on Add), else its "Added" line,
    // else its title, else the card's. force: the focused element is going away.
    restoreFocus(size, force) {
      var active = document.activeElement;
      if (!force && active && active !== document.body && active.isConnected !== false && !this.contains(active)) return;
      if (!force && active && this.contains(active) && active !== this) return;
      var group = size ? this.querySelector('.trc-sleeve-offer__size[data-size="' + size + '"]') : null;
      var target = (group && (group.querySelector('.trc-sleeve-offer__add') || group.querySelector('.trc-sleeve-offer__done') || group.querySelector('.trc-sleeve-offer__size-title'))) ||
        this.querySelector('.trc-sleeve-offer__done') || this.querySelector('.trc-sleeve-offer__size-title') || this.querySelector('.trc-sleeve-offer__heading');
      if (!target) return;
      if (target.tagName !== 'BUTTON') target.setAttribute('tabindex', '-1');
      focusQuietly(target);
    }

    // Adds the packs (/cart/add.js: added to any line of those sleeves, never
    // set). Once the add lands, the cart re-renders the card, which shows
    // "Added" (state.justAdded) and then closes or shows what is left.
    async act(node, request, meta) {
      if (state.inFlight || this._busy || node.getAttribute('aria-disabled') === 'true') return;
      var placement = this.placement;
      state.inFlight = true;
      state.restoreFocus = { placement: placement, size: meta.size };
      this.setBusy(node, true);
      this.lockButtons(true);
      if (this._status) this._status.textContent = '';
      var outcome;
      // The cart this add should leave for the size: its cards and sleeves. A
      // render that finds anything else (a line changed elsewhere) drops the
      // "Added..." line rather than show numbers that are no longer true.
      var perPack = meta.remember && meta.remember.entry ? meta.remember.entry.perPack : 0;
      var expect = meta.info.cards + ':' + (meta.info.sleevesHave + meta.packs * perPack);
      var landed = function () {
        delete state.edits[meta.size];
        state.justAdded = { placement: placement, size: meta.size, packs: meta.packs, at: Date.now(), announced: false, expect: expect };
      };
      try {
        try { outcome = await request(landed); } catch (error) { outcome = { ok: false, unanswered: true, sections: [] }; }
        if (!outcome.ok) {
          // Refused (422: not enough sleeves left) or no answer. Reload the cart so
          // the card shows what the cart holds now and cannot act twice on stale numbers.
          state.pending = { placement: placement, text: COPY.error, tone: 'error' };
          var refreshed = await recover(outcome);
          this.settle(node, outcome.unanswered && !refreshed ? COPY.offline : COPY.error, 'error', outcome.unanswered && !refreshed, placement, meta.size);
          return;
        }
        if (meta.remember) { try { rememberChoice(meta.size, meta.remember.entry, meta.remember.variant, this.readData()); } catch (error) { /* only a default */ } }
        publishAnalytics('trc_sleeve_offer_added', {
          placement: placement,
          position: 'cart',
          size: meta.size,
          packs: meta.packs,
          before: meta.before,
          cards: meta.info.cards,
          needed: meta.recommended != null ? meta.recommended : null,
        });
        // The cart did not re-render this card: count the add here and show it.
        if (this.isConnected && this._result && !this.classList.contains('trc-sleeve-offer--done')) {
          this.addLocal(meta);
          this.render();
        }
      } finally {
        state.inFlight = false;
        if (state.restoreFocus && state.restoreFocus.placement === placement) state.restoreFocus = null;
        unlockAll();
      }
    }

    // The end of a request when the card was not re-rendered by it. hide: the
    // button is not offered again (done, or the cart could not be read).
    settle(node, message, tone, hide, placement, size) {
      if (!this.isConnected) return;
      if (state.pending && state.pending.placement === placement) state.pending = null;
      var hadFocus = document.activeElement === node;
      this.setBusy(node, false);
      if (hide) node.hidden = true;
      this.announce(message, tone);
      if (hide && hadFocus) this.restoreFocus(size);
    }
  }

  function unlockAll() {
    Array.prototype.forEach.call(document.querySelectorAll('trc-sleeve-offer'), function (element) {
      if (typeof element.lockButtons === 'function') element.lockButtons(false);
    });
  }

  // ---------------------------------------------------------------- the popup

  function focusables(root) {
    return Array.prototype.slice.call(root.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(function (node) {
      return !node.disabled && !node.hidden && node.getAttribute('aria-hidden') !== 'true' && !(node.closest && node.closest('[hidden]')) && node.type !== 'hidden' &&
        !(node.type === 'radio' && !node.checked && root.querySelector('input[type="radio"][name="' + node.name + '"]:checked'));
    });
  }

  function reducedMotion() {
    try {
      var theme = window.FoxTheme || {};
      if (theme.config && (theme.config.motionReduced || theme.config.disableAnimations)) return true;
      return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (error) {
      return true;
    }
  }

  function swallowEscapeKeyup() {
    function swallow(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc' && event.code !== 'Escape') return;
      event.stopPropagation();
      stop();
    }
    function stop() {
      window.removeEventListener('keyup', swallow, true);
      window.clearTimeout(timer);
    }
    window.addEventListener('keyup', swallow, true);
    var timer = window.setTimeout(stop, 1500);
  }

  // Opens the popup for a plan. done(choice) runs once, after the popup is
  // gone: 'added', 'declined' or 'failed'. Throws only before anything is shown.
  function openPopup(plan, data, done, opener) {
    if (state.popup) state.popup.close('declined');
    var moneyFormat = data.moneyFormat;
    var inCart = plan.inCart;
    var info = plan.info;
    try { mergeAccountPref(data); } catch (error) { /* only a default */ }
    var current = resolveChoice(info, inCart, choiceFor(info, data));
    if (!current || !selectable(current.variant, inCart)) throw new Error('no sleeves');

    var root = el('div', 'trc-sleeve-popup');
    root.setAttribute('data-size', plan.size);
    var overlay = el('div', 'trc-sleeve-popup__overlay no-empty');
    var panel = el('div', 'trc-sleeve-popup__panel color-scheme-1');
    var titleId = nextId('TrcSleevePopupTitle');
    var leadId = nextId('TrcSleevePopupLead');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', titleId);
    panel.setAttribute('aria-describedby', leadId);
    panel.tabIndex = -1;

    var close = el('button', 'trc-sleeve-popup__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close, no sleeves');
    close.innerHTML = SVG_CLOSE;

    var added = el('p', 'trc-sleeve-popup__added');
    added.insertAdjacentHTML('afterbegin', SVG_CHECK);
    var addedNoun = planNoun(plan);
    added.appendChild(document.createTextNode(addedNoun === 'deck' ? (plan.decks > 1 ? COPY.popupAddedMany : COPY.popupAdded)
      : addedNoun === 'set' ? (plan.decks > 1 ? COPY.popupAddedSets : COPY.popupAddedSet)
        : plan.decks === 2 ? COPY.popupAddedDeckAndSet : COPY.popupAddedMixed));
    var title = el('h2', 'trc-sleeve-popup__title h4', COPY.popupTitle);
    title.id = titleId;
    var lead = el('p', 'trc-sleeve-popup__lead');
    lead.id = leadId;
    var productSlot = el('div', 'trc-sleeve-popup__product');
    var chooseSlot = el('div', 'trc-sleeve-popup__choose');
    var qtyRow = el('div', 'trc-sleeve-popup__qty');
    var qtyLabelId = nextId('TrcSleevePopupPacks');
    var qtyLabel = el('span', 'trc-sleeve-field__label', 'Packs');
    qtyLabel.id = qtyLabelId;
    // The total is in the add button. A pack count change is read out here
    // once the presses stop (the button's name already says it on focus).
    var totalStatus = el('span', 'visually-hidden trc-sleeve-popup__total');
    totalStatus.setAttribute('role', 'status');
    totalStatus.setAttribute('aria-live', 'polite');
    totalStatus.setAttribute('aria-atomic', 'true');
    var totalTimer = null;
    var stockNote = el('p', 'trc-sleeve-popup__stock');
    var message = el('p', 'trc-sleeve-popup__message');
    message.setAttribute('role', 'alert');
    var actions = el('div', 'trc-sleeve-popup__actions');
    var addButton = button('btn btn--primary trc-sleeve-dark trc-sleeve-popup__add', '');
    var declineButton = button('btn btn--secondary trc-sleeve-popup__decline', COPY.popupDecline);
    actions.appendChild(addButton);
    actions.appendChild(declineButton);

    var qty = null;
    var shownSrc = '';
    var preload = null;
    var busy = false;
    var settling = false;
    var finished = false;

    function paint(resetQty) {
      var plan2 = popupRecommend(plan, current.entry, current.variant, inCart);
      lead.textContent = popupCopy(plan, current.entry).lead;
      productSlot.textContent = '';
      var src = current.variant.image || current.entry.image;
      productSlot.appendChild(productBlock(current.entry, current.variant, moneyFormat, false, 72, shownSrc));
      shownSrc = src;
      chooseSlot.textContent = '';
      var picks = chooser(info, current, inCart, function (pick) {
        var focusName = focusedGroup(chooseSlot);
        state.choice[plan.size] = pick;
        current = resolveChoice(info, inCart, pick);
        rememberChoice(plan.size, current.entry, current.variant, data);
        paint(true);
        // Keep focus on the control that changed (the old one was rebuilt).
        refocusGroup(chooseSlot, focusName);
      }, moneyFormat);
      if (picks) chooseSlot.appendChild(picks);
      chooseSlot.hidden = !picks;
      var start = Math.max(1, plan2.offer);
      if (!qty) {
        qty = quantityControl({ min: 1, max: plan2.stockLeft, value: start, label: 'Packs', describedBy: qtyLabelId, onChange: function () { paintTotal(true); } });
        qty.trcInput.setAttribute('aria-labelledby', qtyLabelId);
        qtyRow.appendChild(qtyLabel);
        qtyRow.appendChild(qty);
        qtyRow.appendChild(totalStatus);
      } else {
        qty.trcLimit(plan2.stockLeft);
        if (resetQty) qty.trcSet(start);
      }
      stockNote.textContent = plan2.partial ? 'Only ' + packs(plan2.stockLeft) + ' left.' : '';
      stockNote.hidden = !plan2.partial;
      paintTotal();
    }

    // The add button carries the live total (count x the chosen variant's
    // price). announce: a pack count change, read out politely once it settles.
    function paintTotal(announce) {
      var count = qty.trcGet();
      var cents = current.variant.price;
      setLabel(addButton, addCopy(count, cents, moneyFormat));
      window.clearTimeout(totalTimer);
      totalStatus.textContent = '';
      if (!announce) return;
      totalTimer = window.setTimeout(function () {
        if (!finished) totalStatus.textContent = totalNotice(count, cents, moneyFormat);
      }, 600);
    }

    panel.appendChild(close);
    panel.appendChild(added);
    panel.appendChild(title);
    panel.appendChild(lead);
    panel.appendChild(productSlot);
    panel.appendChild(chooseSlot);
    panel.appendChild(qtyRow);
    panel.appendChild(stockNote);
    panel.appendChild(message);
    panel.appendChild(actions);
    root.appendChild(overlay);
    root.appendChild(panel);
    paint(false);

    var preloadTimer = null;
    var previousFocus = opener || document.activeElement;
    var lockClass = 'trc-sleeve-popup-open';

    function onKey(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        if (busy) return;
        // The theme closes every open modal on the Escape keyup: keep that
        // keyup from closing the cart drawer this popup is about to open.
        swallowEscapeKeyup();
        finish('declined');
        return;
      }
      if (event.key !== 'Tab') return;
      var list = focusables(panel);
      if (!list.length) { event.preventDefault(); panel.focus(); return; }
      var first = list[0];
      var last = list[list.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    function onFocusIn(event) {
      if (!finished && !panel.contains(event.target)) panel.focus();
    }

    function finish(outcome) {
      if (finished) return;
      finished = true;
      if (state.popup === api) state.popup = null;
      if (preload) { preload.cancel(); preload = null; }
      window.clearTimeout(preloadTimer);
      window.clearTimeout(totalTimer);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocusIn, true);
      root.classList.remove('is-open');
      var removed = false;
      function remove() {
        if (removed) return;
        removed = true;
        if (root.parentNode) root.parentNode.removeChild(root);
        document.documentElement.classList.remove(lockClass);
        if (document.body) document.body.classList.remove(lockClass);
        try { done(outcome, previousFocus); } catch (error) { /* the flow goes on */ }
      }
      if (reducedMotion()) remove();
      else {
        root.addEventListener('transitionend', function (event) { if (event.target === panel) remove(); });
        window.setTimeout(remove, 320);
      }
      if (outcome === 'declined') publishAnalytics('trc_sleeve_popup_declined', { size: plan.size, cards: plan.deckCards });
    }

    overlay.addEventListener('click', function () { if (!busy) finish('declined'); });
    close.addEventListener('click', function () { if (!busy) finish('declined'); });
    declineButton.addEventListener('click', function () { if (!busy) finish(message.getAttribute('data-lost') ? 'failed' : 'declined'); });
    addButton.addEventListener('click', async function () {
      // aria-disabled stays set until the outcome is known and handled: no
      // second add while the first may still land.
      if (busy || settling || state.inFlight || addButton.getAttribute('aria-disabled') === 'true') return;
      busy = true;
      settling = true;
      state.inFlight = true;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.classList.add('btn--loading');
      message.textContent = '';
      var count = qty.trcGet();
      var outcome;
      var refreshed = false;
      try {
        try { outcome = await cartRequest('add', current.variant.id, count); } catch (error) { outcome = { ok: false, unanswered: true, sections: [] }; }
        // The request is answered (or given up on): the shopper may decline now.
        busy = false;
        if (outcome.ok) {
          try { rememberChoice(plan.size, current.entry, current.variant, data); } catch (error) { /* only a default */ }
          publishAnalytics('trc_sleeve_popup_added', { size: plan.size, packs: count, recommended: popupRecommend(plan, current.entry, current.variant, inCart).packs, cards: plan.deckCards });
          finish('added');
          return;
        }
        if (outcome.unanswered) {
          // Hidden at once: the add may have landed.
          addButton.hidden = true;
          message.setAttribute('data-lost', 'true');
        }
        refreshed = await recover(outcome);
      } finally {
        busy = false;
        settling = false;
        state.inFlight = false;
        // The cards may have re-rendered (locked) while this add was out.
        unlockAll();
        addButton.classList.remove('btn--loading');
        if (!addButton.hidden) addButton.removeAttribute('aria-disabled');
      }
      if (finished) return;
      if (outcome.unanswered) {
        // The add may have landed: no second add from this popup.
        message.textContent = COPY.popupOffline;
        setLabel(declineButton, COPY.popupContinue);
        declineButton.focus();
      } else {
        message.textContent = COPY.error;
        if (refreshed) addButton.focus();
      }
    });

    var api = { close: finish, root: root, panel: panel };
    try {
      document.body.appendChild(root);
      // The stylesheet loads without blocking the page: never show an
      // unstyled popup (the normal flow takes over instead).
      var position = window.getComputedStyle ? window.getComputedStyle(root).position : 'fixed';
      if (position !== 'fixed') throw new Error('sleeve styles not loaded');
      document.documentElement.classList.add(lockClass);
      document.body.classList.add(lockClass);
      document.addEventListener('keydown', onKey, true);
      document.addEventListener('focusin', onFocusIn, true);
      // Paint once closed, then open, so the transition runs.
      void root.offsetWidth;
      root.classList.add('is-open');
      panel.focus();
      // Once the popup is up (after its opening transition), the thumbnails a
      // brand or color switch would show load in the background.
      preloadTimer = window.setTimeout(function () {
        if (finished) return;
        preload = preloader();
        preload.add(switchImages(info, current, inCart, 72));
      }, reducedMotion() ? 0 : 350);
    } catch (error) {
      // Leave nothing behind: the normal flow takes over.
      finished = true;
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocusIn, true);
      if (root.parentNode) root.parentNode.removeChild(root);
      document.documentElement.classList.remove(lockClass);
      if (document.body) document.body.classList.remove(lockClass);
      throw error;
    }
    state.popup = api;
    publishAnalytics('trc_sleeve_popup_shown', { size: plan.size, cards: plan.deckCards, packs: Math.max(1, popupRecommend(plan, current.entry, current.variant, inCart).offer) });
    return api;
  }

  // ------------------------------------------------------- after a deck add

  // Holds the theme's drawer open (ProductForm calls drawer.show() right after
  // product-ajax:added) until the popup is answered. release(true) opens it.
  function holdDrawer(drawer) {
    var hold = { called: false, args: null, released: false, onShow: null };
    var hadOwn = Object.prototype.hasOwnProperty.call(drawer, 'show');
    var own = drawer.show;
    drawer.show = function () {
      if (hold.released) return own.apply(drawer, arguments);
      hold.called = true;
      hold.args = arguments;
      if (hold.onShow) { var run = hold.onShow; hold.onShow = null; run(); }
      return Promise.resolve();
    };
    function restore() {
      if (hadOwn) drawer.show = own;
      else { try { delete drawer.show; } catch (error) { drawer.show = own; } }
    }
    hold.release = function (open) {
      if (hold.released) return;
      hold.released = true;
      window.clearTimeout(hold.timer);
      restore();
      if (open && hold.called) {
        try { drawer.show.apply(drawer, hold.args || []); } catch (error) { /* the theme's own drawer */ }
      }
    };
    // ProductForm (or the quick view, after it closes) calls show() soon. If
    // nothing does, let go without a popup.
    hold.timer = window.setTimeout(function () { if (!hold.called) hold.release(false); }, TIMEOUTS.drawer);
    return hold;
  }

  function drawerOffer() {
    return document.querySelector('cart-drawer trc-sleeve-offer[data-placement="drawer"]') || document.querySelector('trc-sleeve-offer[data-placement="drawer"]');
  }

  function drawerOfferData() {
    var offer = drawerOffer();
    if (!offer) return null;
    if (typeof offer.readData === 'function') return offer.readData();
    var source = offer.querySelector('script[data-trc-sleeve-data]');
    try { return source ? JSON.parse(source.textContent) : null; } catch (error) { return null; }
  }

  // The snippet prints an empty marker when there is nothing to offer:
  // 'off' (no sleeve product can be sold) or 'empty' (the cart was empty).
  function drawerOfferState() {
    var offer = drawerOffer();
    return offer ? text(offer.getAttribute('data-trc-sleeve-state')) : '';
  }

  function dataHasItems(data, items) {
    var lines = data && Array.isArray(data.lines) ? data.lines : [];
    return items.some(function (item) {
      return lines.some(function (line) { return item.key != null ? text(item.key) === text(line.key) : text(item.variant_id) === text(line.variantId); });
    });
  }

  // The sleeve data from a fresh render of the drawer section (timed).
  async function loadSectionData() {
    var sections = groupedSections();
    if (!sections.length) return null;
    var payload = await fetchSections(sections, TIMEOUTS.section);
    for (var index = 0; index < sections.length; index++) {
      var html = payload && payload[sections[index]];
      if (!html) continue;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var source = doc.querySelector('trc-sleeve-offer[data-placement="drawer"] script[data-trc-sleeve-data]');
      if (source) return JSON.parse(source.textContent);
    }
    return null;
  }

  // The cart's line quantities by key before an add, from the drawer's sleeve
  // data, or null when unknown. An empty cart is {}.
  function lineQuantities() {
    var data = drawerOfferData();
    if (data && Array.isArray(data.lines)) {
      var map = {};
      data.lines.forEach(function (line) {
        if (line && line.key != null) map[text(line.key)] = (map[text(line.key)] || 0) + quantityOf(line.quantity);
      });
      return map;
    }
    return drawerOfferState() === 'empty' ? {} : null;
  }

  // A product form (product page, quick view) sending an add, in the capture
  // phase before the theme's own handler: notes the variant ids, the quantity
  // it asks for and the cart's lines before it. It never opens anything; only
  // the popup after product-ajax:added reads it.
  function onFormSubmit(event) {
    var form = event.target;
    if (!form || String(form.tagName).toUpperCase() !== 'FORM') return;
    if (!/\/cart\/add\b/.test(text(form.getAttribute('action')))) return;
    var fields = new FormData(form);
    var ids = fields.getAll('id').map(text).filter(Boolean);
    if (!ids.length) return;
    state.sent = { at: Date.now(), ids: ids, quantity: positiveInt(fields.get('quantity'), MAX_CARDS_PER_DECK) || 1, before: lineQuantities() };
  }

  // The added items with what this add put in: requested (the quantity the
  // form sent) and before (the line's quantity before the add), when the last
  // form add was for that variant. Takes the form add, so it is used once.
  function withSent(items) {
    var sent = state.sent;
    state.sent = null;
    var age = sent ? Date.now() - sent.at : -1;
    if (!sent || !(age >= 0 && age < SENT_TTL_MS)) return items;
    return items.map(function (item) {
      if (sent.ids.indexOf(text(item.variant_id)) === -1) return item;
      var copy = Object.assign({}, item, { requested: sent.quantity });
      if (sent.before && item.key != null) copy.before = sent.before[text(item.key)] || 0;
      return copy;
    });
  }

  // A deck from the add answer alone: an "_Input method" property or a deck product type.
  function looksLikeDeck(item) {
    var given = (item && item.properties) || null;
    var method = given && typeof given === 'object' ? given['_Input method'] : '';
    return Boolean(text(method)) || DECK_TYPES.indexOf(lower(item && item.product_type)) !== -1;
  }

  function onDeckAdded(event) {
    var items = withSent(addedItems(event.detail));
    if (!items.length) return;
    if (document.body && document.body.classList.contains('cart-template')) return;
    // One popup at a time: a second add while one is open (or waiting) is
    // left to the open one, whose hold also takes the theme's next show().
    if (state.popup || (state.hold && !state.hold.released)) return;
    // No sleeves can be sold: the drawer opens at once, untouched.
    if (drawerOfferState() === 'off') return;
    var drawer = document.querySelector('cart-drawer');
    var data = drawerOfferData();
    if (!drawer || typeof drawer.show !== 'function') return;
    var fresh = data && dataHasItems(data, items);
    var plan = null;
    try { plan = fresh ? popupPlan(data, items) : null; } catch (error) { plan = null; }
    if (fresh && !plan) return; // nothing to offer: the normal flow, untouched
    // Not in the drawer's data yet: only a deck add is worth a fresh read.
    if (!fresh && !items.some(looksLikeDeck)) return;

    var hold = holdDrawer(drawer);
    state.hold = hold;
    var opened = false;

    function show(planNow, dataNow) {
      if (opened) return;
      opened = true;
      try {
        openPopup(planNow, dataNow, function (outcome, opener) {
          hold.release(true);
          if (!hold.called && opener && typeof opener.focus === 'function') {
            try { opener.focus(); } catch (error) { /* gone */ }
          }
        }, hold.args && hold.args[0]);
      } catch (error) {
        hold.release(true);
      }
    }

    function whenShowCalled(run) {
      if (hold.released) return;
      if (hold.called) run();
      else hold.onShow = run;
    }

    if (plan) {
      whenShowCalled(function () { show(plan, data); });
      return;
    }
    // The drawer did not re-render with the new line: read a fresh render (timed).
    loadSectionData().then(function (loaded) {
      var next = loaded && dataHasItems(loaded, items) ? popupPlan(loaded, items) : null;
      if (!next) { hold.release(true); return; }
      whenShowCalled(function () { show(next, loaded); });
    }).catch(function () { hold.release(true); });
  }

  // The product handle in a /products/<handle> or /collections/x/products/<handle> path.
  function productHandle(path) {
    var match = /\/products\/([^\/?#]+)/.exec(text(path));
    if (!match) return '';
    try { return decodeURIComponent(match[1]).toLowerCase(); } catch (error) { return match[1].toLowerCase(); }
  }

  function shown(node) {
    if (!node || node.hidden || (node.closest && node.closest('[hidden], [inert]'))) return false;
    try {
      var style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
    } catch (error) { /* treat as shown */ }
    return true;
  }

  // Another overlay is up: the image deck editor, the empty slots overlay,
  // any modal dialog, the cart drawer, the quick view or any theme modal.
  function otherOverlayOpen() {
    var body = document.body;
    if (body && (body.classList.contains('modal-show') || body.classList.contains('modal-showing'))) return true;
    var selectors = '.trc-deck-editor, .trc-empty-slots-overlay, [aria-modal="true"], dialog[open], cart-drawer[open], quick-view-modal[open], .drawer[open], .modal[open], modal-component[open], basic-modal[open], drawer-component[open]';
    var nodes = document.querySelectorAll(selectors);
    for (var index = 0; index < nodes.length; index++) {
      var node = nodes[index];
      if (node.closest && node.closest('.trc-sleeve-popup')) continue;
      if (shown(node)) return true;
    }
    return false;
  }

  // Focus is in a text field or an editable area.
  function typingSomewhere() {
    var active = document.activeElement;
    if (!active || active === document.body) return false;
    if (active.isContentEditable) return true;
    var tag = (active.tagName || '').toLowerCase();
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    return !/^(?:button|submit|reset|checkbox|radio|range|color|file|image|hidden)$/i.test(active.type || 'text');
  }

  // A custom image deck uploaded in the background reaches the cart later,
  // with no drawer opening. If the shopper is still on that deck's page, the
  // popup shows then, by the same rules; anywhere else the cart card covers it.
  function onBackgroundAdded(event) {
    var detail = event.detail || {};
    if (!detail.jobId || state.popup || (state.hold && !state.hold.released)) return;
    if (document.body && document.body.classList.contains('cart-template')) return;
    var cartItems = detail.cart && Array.isArray(detail.cart.items) ? detail.cart.items : [];
    var item = cartItems.filter(function (line) {
      return line && line.properties && text(line.properties['_Upload job ID']) === text(detail.jobId);
    })[0];
    if (!item) return;
    var data = drawerOfferData();
    var items = [{ key: item.key, variant_id: item.variant_id }];
    if (!data || !dataHasItems(data, items)) return;
    var line = (data.lines || []).filter(function (entry) { return entry && text(entry.key) === text(item.key); })[0];
    var path = '';
    try { path = new URL(text(line && line.url) || text(item.url), window.location.origin).pathname; } catch (error) { path = ''; }
    var handle = productHandle(path);
    if (!handle || handle !== productHandle(window.location.pathname)) return;
    // Never over the image editor, another dialog, the drawer or a modal, and
    // never while the shopper is typing: the cart card covers it then.
    if (otherOverlayOpen() || typingSomewhere()) return;
    var plan = popupPlan(data, items);
    if (!plan) return;
    var opener = document.activeElement;
    try { openPopup(plan, data, function () {}, opener); } catch (error) { /* the cart card covers it */ }
  }

  // ------------------------------------------------------------- listeners

  // Only the theme's Add to cart (ProductForm, the quick view, product
  // bundles) sends product-ajax:added. Buy it now goes straight to checkout:
  // no popup for it.
  document.addEventListener('product-ajax:added', function (event) {
    try { onDeckAdded(event); } catch (error) { /* the normal flow goes on */ }
  });

  document.addEventListener('submit', function (event) {
    try { onFormSubmit(event); } catch (error) { /* the add goes on */ }
  }, true);

  document.addEventListener('trc-background-upload:added', function (event) {
    try { onBackgroundAdded(event); } catch (error) { /* the cart card covers it */ }
  });

  document.addEventListener('visitorConsentCollected', function (event) {
    try { onConsentCollected(event); } catch (error) { /* only a default */ }
  });

  window.TRCSleeveOffer = {
    decodeProof: decodeProof,
    proofCount: proofCount,
    customImagesCount: customImagesCount,
    tagCount: tagCount,
    isDeckLine: isDeckLine,
    isPremadeLine: isPremadeLine,
    cardsForLine: cardsForLine,
    classifySize: classifySize,
    sizeOfLine: sizeOfLine,
    sleeveSizeOf: sleeveSizeOf,
    packSize: packSize,
    colorIndexOf: colorIndexOf,
    buildCatalog: buildCatalog,
    computeSizes: computeSizes,
    recommend: recommend,
    resolveChoice: resolveChoice,
    popupPlan: popupPlan,
    popupRecommend: popupRecommend,
    addedQuantity: addedQuantity,
    needCopy: needCopy,
    popupCopy: popupCopy,
    planNoun: planNoun,
    addCopy: addCopy,
    addedCopy: addedCopy,
    timing: TIMING,
    totalNotice: totalNotice,
    formatMoney: formatMoney,
    addedItems: addedItems,
    addGetsOffer: addGetsOffer,
    openPopup: openPopup,
    timedFetch: timedFetch,
    swatchFor: swatchFor,
    namedColors: NAME_HEX,
    imageSource: imageSource,
    sizedUrl: sizedUrl,
    switchImages: switchImages,
    preloader: preloader,
    cleanPref: cleanPref,
    browserPref: browserPref,
    prefFor: prefFor,
    prefChoice: prefChoice,
    choiceFor: choiceFor,
    rememberChoice: rememberChoice,
    mergeAccountPref: mergeAccountPref,
    sameOriginPath: sameOriginPath,
    prefCookie: PREF_COOKIE,
    copy: COPY,
    state: state,
    DISMISS_KEY: DISMISS_KEY,
    cardAddCopy: cardAddCopy,
    timeouts: TIMEOUTS,
  };

  if (!customElements.get('trc-sleeve-offer')) customElements.define('trc-sleeve-offer', TrcSleeveOffer);
})();
